// ============================================================
// GAME CONSTANTS
// ============================================================

export const TILE_SIZE = 8; // 8x8 pixel tiles (sprites are 8x16 = 1x2 tiles)
export const CHUNK_SIZE = 8; // tiles per chunk
export const CHUNKS_LOADED = 5;
export const VIEWPORT_TILES = CHUNKS_LOADED * CHUNK_SIZE - 12; // 28 tiles visible (reduced by 4)
export const UNDERGROUND_LEVEL = 13; // worldY where sky turns black and water fills to

// ============================================================
// SPAWN CLEARING — cosmology pixel alignment
// 60 tiles left of reference pixel (33, 57). Spawn platform and
// exploration house are both left of Yggdrasil trunk (pixels 35-37).
// ============================================================
export const SPAWN_CENTER_X = -102; // world X of spawn clearing center

export const PLAYER_HEIGHT_STANDING = 2; // tiles tall when standing
export const PLAYER_HEIGHT_CROUCHING = 1; // tiles tall when crouching

export const MOVE_RATE = 10; // tiles per second
export const MOVE_INTERVAL = 1000 / MOVE_RATE; // ms between moves
export const JUMP_HEIGHT = 3; // tiles
export const MIN_JUMP_TILES = 1; // minimum jump even if up is released
export const JUMPSQUAT_DURATION = 100; // ms

// Network
export const TICK_RATE = 10; // server ticks per second
export const TICK_INTERVAL = 1000 / TICK_RATE;

// ============================================================
// COMBAT CONSTANTS
// ============================================================

export const HP_DEFAULT = 3;
export const SWORD_OFFSET = 1;           // sword at x ± 1
export const SLASH_SPAWN_OFFSET = 2;     // slash spawns at x ± 2
export const SLASH_RATE = 3;             // slash every 3 ticks (300ms) - matches duration for 1 slash max
export const SLASH_DURATION = 3;         // travels 3 tiles
export const SWORD_DAMAGE = 1;           // damage per tick
export const SLASH_DAMAGE = 1;           // damage on hit

// ============================================================
// RUNE CONSTANTS
// ============================================================

export const HIEROGLYPH_COUNT = 100;     // 0-99 hieroglyph variants
export const COMBO_MAX_LENGTH = 3;       // max runes in portal combo
export const RUNE_STRIKE_COOLDOWN_TICKS = 3; // ticks between rune strikes
export const SWORD_DESTROY_COOLDOWN_TICKS = 3; // ticks between sword tile destructions
export const PORTAL_GRACE_MS = 500;      // ms after spawn before portal activates

// ============================================================
// TILE TYPES
// ============================================================

export enum TileType {
    SKY = 0,
    DIRT = 1,
    STONE = 2,
    GRASS = 3,
    RUNE = 4,  // static like STONE, stores hieroglyph separately
    PORTAL = 5,  // purple portal door frame tiles (non-solid, visual only)
    SKULL = 6,  // dropped on player death, falls like dirt
    HEART_CONTAINER = 7,  // light green apple: heals 1 HP on pickup, falls like dirt, spawns from leaves
    BIG_HEART_CONTAINER = 8,  // consumable: increases max HP by 1 and fully heals, does not fall
    LADDER = 9,  // climbable: solid for feet, passable for body/head, no gravity
    STICK = 10,  // wooden stick: climbable like ladder/leaves, falls like dirt, two sticks combine into a ladder
    LEAVES = 11, // climbable like ladder, no gravity; falling block impact evaporates stack into sticks
    TRUNK = 12,  // dark brown tree trunk: non-solid (passable like air), no gravity
    LOG = 13,    // lighter brown log cross-section: solid, falls like dirt
    FLINT = 14,  // purple-grey flint: solid, falls like dirt, used for tool crafting
    PLANK = 15,  // tan plank: solid, no gravity, pickupable; created by striking LOG with sword
    AMETHYST = 16,  // purple amethyst block: solid, no gravity
    OPAL = 17,      // opal-colored block: solid, no gravity
    TURQUOISE = 18, // turquoise block: solid, no gravity
    SAPLING = 19,   // light green sapling: non-solid (passable like trunk), no gravity
    RATS_NEST = 20, // dark grey rat's nest: solid, no gravity
    WATER = 21,     // blue water: climbable like ladder, not breakable, fills valleys
    WATER_SURFACE = 22, // topmost water tile: visually identical to WATER but passable like SKY (can't stand on it)
    WOODEN_SHIELD = 23, // wooden shield block: solid, falls like dirt, pickupable
    STONE_CLUB = 24,    // stone club block: solid, falls like dirt, pickupable
    IRON_SWORD = 25,    // iron sword block: solid, falls like dirt, pickupable
    RED_STONE = 26,     // red stone block (kiwen pilin): solid, no gravity, exploration house material
    BOOTS = 27,         // dark blue boots (noka sewi): consumable, grants jump height 3 on pickup
    YELLOW_DOOR = 28,   // solid yellow door (lupa jelo): impassable, unlocked by YELLOW_KEY pickup
    YELLOW_DOOR_OPEN = 29, // opened yellow door (lupa jelo open): passable, dark grey with yellow rim
    YELLOW_KEY = 30,    // yellow key (ilo jelo): passable pickup, unlocks all YELLOW_DOOR → YELLOW_DOOR_OPEN
    // Placeholder tiles (command-spawned only)
    ILO_A = 31,
    ILO_AKESI = 32,
    ILO_ALA = 33,
    ILO_ALASA = 34,
    ILO_ALE = 35,
    ILO_ANPA = 36,
    ILO_ANU = 37,
    ILO_AWEN = 38,
    ILO_EN = 39,
    ILO_ESUN = 40,
    ILO_IJO = 41,
    ILO_ILO = 42,
    ILO_JAKI = 43,
    ILO_JAN = 44,
    ILO_JO = 45,
    ILO_KALA = 46,
    ILO_KALAMA = 47,
    ILO_KAMA = 48,
    ILO_KASI = 49,
    ILO_KEN = 50,
    ILO_KEPEKEN = 51,
    ILO_KILI = 52,
    ILO_KIWEN = 53,
    ILO_KON = 54,
    ILO_KULE = 55,
    ILO_KUTE = 56,
    ILO_LA = 57,
    ILO_LAPE = 58,
    ILO_LEN = 59,
    ILO_LETE = 60,
    ILO_LI = 61,
    ILO_LILI = 62,
    ILO_LIPU = 63,
    ILO_LOJE = 64,
    ILO_LON = 65,
    ILO_LUKA = 66,
    ILO_LUKIN = 67,
    ILO_LUPA = 68,
    ILO_MA = 69,
    ILO_MAMA = 70,
    ILO_MANI = 71,
    ILO_MIJE = 72,
    ILO_MOKU = 73,
    ILO_MOLI = 74,
    ILO_MU = 75,
    ILO_MUN = 76,
    ILO_NASIN = 77,
    ILO_NIMI = 78,
    ILO_NOKA = 79,
    ILO_O = 80,
    ILO_OLIN = 81,
    ILO_OPEN = 82,
    ILO_PALISA = 83,
    ILO_PAN = 84,
    ILO_PILIN = 85,
    ILO_PIPI = 86,
    ILO_POKA = 87,
    ILO_POKI = 88,
    ILO_SELI = 89,
    ILO_SELO = 90,
    ILO_SEME = 91,
    ILO_SIKE = 92,
    ILO_SITELEN = 93,
    ILO_SOWELI = 94,
    ILO_SUNO = 95,
    ILO_TENPO = 96,
    ILO_TOKI = 97,
    ILO_TU = 98,
    ILO_UTA = 99,
    ILO_UTALA = 100,
    ILO_WAN = 101,
    ILO_WASO = 102,
    KASI_AKESI = 103,
    KASI_ALE = 104,
    KASI_ANU = 105,
    KASI_EN = 106,
    KASI_IJO = 107,
    KASI_JAN = 108,
    KASI_JELO = 109,
    KASI_KAMA = 110,
    KASI_KEPEKEN = 111,
    KASI_KILI = 112,
    KASI_KIWEN = 113,
    KASI_LASO = 114,
    KASI_LAWA = 115,
    KASI_LI = 116,
    KASI_LINJA = 117,
    KASI_LUKA = 118,
    KASI_MA = 119,
    KASI_MANI = 120,
    KASI_MELI = 121,
    KASI_MI = 122,
    KASI_MOLI = 123,
    KASI_MONSI = 124,
    KASI_MU = 125,
    KASI_MUN = 126,
    KASI_MUTE = 127,
    KASI_NOKA = 128,
    KASI_O = 129,
    KASI_OLIN = 130,
    KASI_PAKALA = 131,
    KASI_PAN = 132,
    KASI_PANA = 133,
    KASI_PIMEJA = 134,
    KASI_PIPI = 135,
    KASI_POKI = 136,
    KASI_SELI = 137,
    KASI_SEME = 138,
    KASI_SIKE = 139,
    KASI_SUNO = 140,
    KASI_SUWI = 141,
    KASI_TAWA = 142,
    KASI_TELO = 143,
    KASI_TOMO = 144,
    KASI_UTA = 145,
    KASI_UTALA = 146,
    KASI_WASO = 147,
    KASI_WAWA = 148,
    KILI_AKESI = 149,
    KILI_ALE = 150,
    KILI_ANPA = 151,
    KILI_ESUN = 152,
    KILI_IJO = 153,
    KILI_IKE = 154,
    KILI_INSA = 155,
    KILI_JAKI = 156,
    KILI_JELO = 157,
    KILI_KEN = 158,
    KILI_KILI = 159,
    KILI_KIWEN = 160,
    KILI_KULUPU = 161,
    KILI_LASO = 162,
    KILI_LAWA = 163,
    KILI_LETE = 164,
    KILI_LINJA = 165,
    KILI_LOJE = 166,
    KILI_MA = 167,
    KILI_MOLI = 168,
    KILI_MUSI = 169,
    KILI_MUTE = 170,
    KILI_NANPA = 171,
    KILI_OPEN = 172,
    KILI_PAKALA = 173,
    KILI_PALISA = 174,
    KILI_PAN = 175,
    KILI_PU = 176,
    KILI_SELI = 177,
    KILI_SELO = 178,
    KILI_SONA = 179,
    KILI_SOWELI = 180,
    KILI_SUWI = 181,
    KILI_TELO = 182,
    KILI_UNPA = 183,
    KILI_WILE = 184,
    KIWEN_ANPA = 185,
    KIWEN_IJO = 186,
    KIWEN_JAN = 187,
    KIWEN_JELO = 188,
    KIWEN_JO = 189,
    KIWEN_KALA = 190,
    KIWEN_KALAMA = 191,
    KIWEN_KAMA = 192,
    KIWEN_KO = 193,
    KIWEN_KULUPU = 194,
    KIWEN_KUTE = 195,
    KIWEN_LA = 196,
    KIWEN_LEN = 197,
    KIWEN_LETE = 198,
    KIWEN_LI = 199,
    KIWEN_LIPU = 200,
    KIWEN_LUKA = 201,
    KIWEN_LUKIN = 202,
    KIWEN_MAMA = 203,
    KIWEN_MANI = 204,
    KIWEN_MI = 205,
    KIWEN_MOKU = 206,
    KIWEN_MOLI = 207,
    KIWEN_NASIN = 208,
    KIWEN_NI = 209,
    KIWEN_NIMI = 210,
    KIWEN_NOKA = 211,
    KIWEN_O = 212,
    KIWEN_OLIN = 213,
    KIWEN_ONA = 214,
    KIWEN_PAKALA = 215,
    KIWEN_PAN = 216,
    KIWEN_PANA = 217,
    KIWEN_PI = 218,
    KIWEN_PIMEJA = 219,
    KIWEN_PIPI = 220,
    KIWEN_POKI = 221,
    KIWEN_PU = 222,
    KIWEN_SELI = 223,
    KIWEN_SEME = 224,
    KIWEN_SIJELO = 225,
    KIWEN_SOWELI = 226,
    KIWEN_SUNO = 227,
    KIWEN_SUPA = 228,
    KIWEN_TOKI = 229,
    KIWEN_TOMO = 230,
    KIWEN_UNPA = 231,
    KIWEN_WAWA = 232,
    // kiwen (blocks) — expanded set
    KIWEN_A = 264,
    KIWEN_AKESI = 265,
    KIWEN_ALA = 266,
    KIWEN_ALASA = 267,
    KIWEN_ALE = 268,
    KIWEN_ANTE = 269,
    KIWEN_ANU = 270,
    KIWEN_AWEN = 271,
    KIWEN_E = 272,
    KIWEN_EN = 273,
    KIWEN_ESUN = 274,
    KIWEN_IKE = 275,
    KIWEN_JAKI = 276,
    KIWEN_KEN = 277,
    KIWEN_KEPEKEN = 278,
    KIWEN_KILI = 279,
    KIWEN_KON = 280,
    KIWEN_LAPE = 281,
    KIWEN_LILI = 282,
    KIWEN_LINJA = 283,
    KIWEN_LOJE = 284,
    KIWEN_LON = 285,
    KIWEN_MELI = 286,
    KIWEN_MIJE = 287,
    KIWEN_MONSI = 288,
    KIWEN_MU = 289,
    KIWEN_MUN = 290,
    KIWEN_MUSI = 291,
    KIWEN_MUTE = 292,
    KIWEN_NANPA = 293,
    KIWEN_NASA = 294,
    KIWEN_NENA = 295,
    KIWEN_PALI = 296,
    KIWEN_PALISA = 297,
    KIWEN_PINI = 298,
    KIWEN_POKA = 299,
    KIWEN_PONA = 300,
    KIWEN_SAMA = 301,
    KIWEN_SELO = 302,
    KIWEN_SIKE = 303,
    KIWEN_SIN = 304,
    KIWEN_SINA = 305,
    KIWEN_SINPIN = 306,
    KIWEN_SITELEN = 307,
    KIWEN_SONA = 308,
    KIWEN_SULI = 309,
    KIWEN_SUWI = 310,
    KIWEN_TAN = 311,
    KIWEN_TASO = 312,
    KIWEN_TAWA = 313,
    KIWEN_TELO = 314,
    KIWEN_TENPO = 315,
    KIWEN_TU = 316,
    KIWEN_UTA = 317,
    KIWEN_UTALA = 318,
    KIWEN_WALO = 319,
    KIWEN_WAN = 320,
    KIWEN_WASO = 321,
    KIWEN_WEKA = 322,
    KIWEN_WILE = 323,
    // kiwen — 12 word slots (ilo, insa, kasi, kiwen, kule, laso, lawa, lupa, ma, open, pilin, sewi)
    // are covered by core tiles (FLINT, AMETHYST, GRASS, STONE, OPAL, TURQUOISE, SKULL, PORTAL, DIRT, RUNE, RED_STONE, SKY)
    // ko (sands) — gravity blocks
    KO_ESUN = 233,
    KO_IJO = 234,
    KO_IKE = 235,     // Quicksand: prevents step-up, traps player
    KO_ILO = 236,
    KO_JAKI = 237,
    KO_JAN = 238,
    KO_JELO = 239,
    KO_KASI = 240,
    KO_KIWEN = 241,
    KO_KO = 242,
    KO_KULE = 243,
    KO_KULUPU = 244,
    KO_LAPE = 245,
    KO_LILI = 246,
    KO_LOJE = 247,
    KO_MOKU = 248,
    KO_MUN = 249,
    KO_NI = 250,
    KO_O = 251,
    KO_PAN = 252,
    KO_PIMEJA = 253,
    KO_PU = 254,
    KO_SELI = 255,
    KO_SEWI = 256,
    KO_SUWI = 257,
    KO_TELO = 258,
    KO_TENPO = 259,
    KO_UNPA = 260,
    KO_UTALA = 261,
    KO_WALO = 262,
    KO_WASO = 263,
    // kili (fruits) — expanded set (fills remaining 84 of 120 kili entries)
    KILI_A = 324,
    KILI_ALA = 325,
    KILI_ALASA = 326,
    KILI_ANTE = 327,
    KILI_ANU = 328,
    KILI_AWEN = 329,
    KILI_E = 330,
    KILI_EN = 331,
    KILI_ILO = 332,
    KILI_JAN = 333,
    KILI_JO = 334,
    KILI_KALA = 335,
    KILI_KALAMA = 336,
    KILI_KAMA = 337,
    // KILI_KASI covered by HEART_CONTAINER (kili kasi)
    KILI_KEPEKEN = 339,
    KILI_KO = 340,
    KILI_KON = 341,
    KILI_KULE = 342,
    KILI_KUTE = 343,
    KILI_LA = 344,
    KILI_LAPE = 345,
    KILI_LEN = 346,
    KILI_LI = 347,
    KILI_LILI = 348,
    KILI_LIPU = 349,
    KILI_LON = 350,
    KILI_LUKA = 351,
    KILI_LUKIN = 352,
    KILI_LUPA = 353,
    KILI_MAMA = 354,
    KILI_MANI = 355,
    KILI_MELI = 356,
    KILI_MI = 357,
    KILI_MIJE = 358,
    KILI_MOKU = 359,
    KILI_MONSI = 360,
    KILI_MU = 361,
    KILI_MUN = 362,
    KILI_NASA = 363,
    KILI_NASIN = 364,
    KILI_NENA = 365,
    KILI_NI = 366,
    KILI_NIMI = 367,
    KILI_NOKA = 368,
    KILI_O = 369,
    KILI_OLIN = 370,
    KILI_ONA = 371,
    KILI_PALI = 372,
    KILI_PANA = 373,
    KILI_PI = 374,
    KILI_PILIN = 375,
    KILI_PIMEJA = 376,
    KILI_PINI = 377,
    KILI_PIPI = 378,
    KILI_POKA = 379,
    KILI_POKI = 380,
    KILI_PONA = 381,
    KILI_SAMA = 382,
    KILI_SEME = 383,
    KILI_SEWI = 384,
    KILI_SIJELO = 385,
    KILI_SIKE = 386,
    KILI_SIN = 387,
    KILI_SINA = 388,
    KILI_SINPIN = 389,
    KILI_SITELEN = 390,
    KILI_SULI = 391,
    KILI_SUNO = 392,
    KILI_SUPA = 393,
    KILI_TAN = 394,
    KILI_TASO = 395,
    KILI_TAWA = 396,
    KILI_TENPO = 397,
    KILI_TOKI = 398,
    KILI_TOMO = 399,
    KILI_TU = 400,
    KILI_UTA = 401,
    KILI_UTALA = 402,
    KILI_WALO = 403,
    KILI_WAN = 404,
    KILI_WASO = 405,
    KILI_WAWA = 406,
    KILI_WEKA = 407,
    // kasi (plants) — expanded set
    KASI_A = 408,
    KASI_ALA = 409,
    KASI_ALASA = 410,
    KASI_ANPA = 411,
    KASI_ANTE = 412,
    KASI_AWEN = 413,
    KASI_E = 414,
    KASI_ESUN = 415,
    KASI_IKE = 416,
    KASI_ILO = 417,
    // KASI_INSA covered by LOG (kasi insa)
    KASI_JAKI = 419,
    KASI_JO = 420,
    KASI_KALA = 421,
    KASI_KALAMA = 422,
    KASI_KASI = 423,
    KASI_KEN = 424,
    KASI_KO = 425,
    KASI_KON = 426,
    KASI_KULE = 427,
    KASI_KULUPU = 428,
    KASI_KUTE = 429,
    KASI_LA = 430,
    KASI_LAPE = 431,
    KASI_LEN = 432,
    KASI_LETE = 433,
    // KASI_LILI covered by SAPLING (kasi lili)
    // KASI_LIPU covered by LEAVES (kasi lipu)
    KASI_LOJE = 436,
    KASI_LON = 437,
    KASI_LUKIN = 438,
    KASI_LUPA = 439,
    KASI_MAMA = 440,
    KASI_MIJE = 441,
    KASI_MOKU = 442,
    KASI_MUSI = 443,
    KASI_NANPA = 444,
    KASI_NASA = 445,
    KASI_NASIN = 446,
    KASI_NENA = 447,
    KASI_NI = 448,
    KASI_NIMI = 449,
    KASI_ONA = 450,
    KASI_OPEN = 451,
    KASI_PALI = 452,
    // KASI_PALISA covered by TRUNK (kasi palisa)
    KASI_PI = 454,
    KASI_PILIN = 455,
    KASI_PINI = 456,
    KASI_POKA = 457,
    KASI_PONA = 458,
    KASI_PU = 459,
    KASI_SAMA = 460,
    KASI_SELO = 461,
    KASI_SEWI = 462,
    KASI_SIJELO = 463,
    KASI_SIN = 464,
    KASI_SINA = 465,
    KASI_SINPIN = 466,
    KASI_SITELEN = 467,
    KASI_SONA = 468,
    KASI_SOWELI = 469,
    KASI_SULI = 470,
    // KASI_SUPA covered by PLANK (kasi supa)
    KASI_TAN = 472,
    KASI_TASO = 473,
    KASI_TENPO = 474,
    KASI_TOKI = 475,
    KASI_TU = 476,
    KASI_UNPA = 477,
    KASI_WALO = 478,
    KASI_WAN = 479,
    KASI_WEKA = 480,
    KASI_WILE = 481,
}

export const TILE_COLORS: Record<number, number> = {
    [TileType.SKY]: 0x87CEEB,
    [TileType.DIRT]: 0x8B4513,
    [TileType.STONE]: 0x696969,
    [TileType.GRASS]: 0x228B22,
    [TileType.RUNE]: 0x555577,
    [TileType.PORTAL]: 0x8B008B,  // Dark magenta/purple
    [TileType.SKULL]: 0xFFFFFF,   // White (fallback color)
    [TileType.HEART_CONTAINER]: 0x90EE90,  // Light green (Apple)
    [TileType.BIG_HEART_CONTAINER]: 0x8B008B,  // Purple
    [TileType.LADDER]: 0xFFFF00,  // Yellow
    [TileType.STICK]: 0xD2B48C,   // Light tan brown
    [TileType.LEAVES]: 0x006400,  // Dark green
    [TileType.TRUNK]: 0x3B1F0B,  // Very dark brown
    [TileType.LOG]: 0x5C3317,    // Dark brown (slightly lighter than trunk)
    [TileType.FLINT]: 0x6B5B73,  // Purple-grey
    [TileType.PLANK]: 0xC4A882,  // Tan (processed wood)
    [TileType.AMETHYST]: 0x6B3FA0,  // Purple
    [TileType.OPAL]: 0xA8C3D6,     // Opal (iridescent blue-white)
    [TileType.TURQUOISE]: 0x30D5C8, // Turquoise
    [TileType.SAPLING]: 0x90EE90,   // Light green
    [TileType.RATS_NEST]: 0x404040, // Dark grey
    [TileType.WATER]: 0x1E90FF,    // Dodger blue
    [TileType.WATER_SURFACE]: 0x1E90FF, // Same as WATER visually
    [TileType.WOODEN_SHIELD]: 0x5C3317, // Dark brown (trunk-like)
    [TileType.STONE_CLUB]: 0x808080,    // Grey
    [TileType.IRON_SWORD]: 0xC0C0C0,   // Silver
    [TileType.RED_STONE]: 0xCC3333,    // Red
    [TileType.BOOTS]: 0x00008B,       // Dark blue
    [TileType.YELLOW_DOOR]: 0xFFD700,       // Gold yellow (solid door)
    [TileType.YELLOW_DOOR_OPEN]: 0x404040,  // Dark grey (opened door)
    [TileType.YELLOW_KEY]: 0xFFD700,        // Gold yellow (key pickup)
};

// ============================================================
// PLAYER STATES
// ============================================================

export enum PlayerState {
    IDLE = 'IDLE',
    WALKING = 'WALKING',
    JUMPSQUAT = 'JUMPSQUAT',
    RISING = 'RISING',
    FALLING = 'FALLING',
    CROUCHING = 'CROUCHING',
}

// ============================================================
// INPUT TYPES
// ============================================================

export interface PlayerInput {
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;
    downJustPressed: boolean;
    sequence: number; // For client prediction reconciliation
    timestamp: number;
}

// ============================================================
// NETWORK DATA TYPES
// ============================================================

export interface ChunkData {
    cx: number;
    cy: number;
    tiles: number[][]; // 2D array of TileType
}

export interface BlockUpdate {
    x: number;
    y: number;
    type: TileType;
    action: 'place' | 'remove' | 'fall';
}

/** A rectangle in tile coordinates that occludes sky constellations. */
export interface RoomBox {
    x1: number;  // left tile x (inclusive)
    y1: number;  // top tile y (inclusive)
    x2: number;  // right tile x (inclusive)
    y2: number;  // bottom tile y (inclusive)
}

export interface FallingBlock {
    x: number;
    y: number;
    movedThisTick: boolean;
    /** Horizontal inertia for LOG rolling: 1 (right), -1 (left), or undefined (unset). */
    inertiaDir?: number;
    /** When true, block was just added to the falling list and should wait 1 tick before moving. */
    justAdded?: boolean;
}

// ============================================================
// COMBAT DATA TYPES
// ============================================================

export interface SlashData {
    id: number;
    spawnX: number;
    spawnY: number;
    facing: number;
    spawnTick: number;
    duration: number;
    ownerId: string;
}

// ============================================================
// RODENT (ENEMY) CONSTANTS
// ============================================================

export enum RodentType {
    MOUSE = 'mouse',
    RAT = 'rat',
    SQUIRREL = 'squirrel',
    CRAB = 'crab',
}

export const RODENT_COLORS: Record<string, number> = {
    [RodentType.MOUSE]: 0xffffff,    // white
    [RodentType.RAT]: 0x808080,      // grey
    [RodentType.SQUIRREL]: 0x8B4513, // brown (saddle brown)
    [RodentType.CRAB]: 0xFF0000,     // red
};

export const MOUSE_DAMAGE = 1;           // damage to player per tick of overlap
export const RATS_NEST_TIMER = 20;       // ticks a rodent stays inside a rat's nest before respawning

// ============================================================
// CREATURE CATEGORY SYSTEM
// Extends the 4 legacy RodentTypes with 480 new creature types
// across 4 categories: soweli (mammals), akesi (reptiles),
// kala (fish), waso (birds). Each category has 120 entries
// (one per Toki Pona modifier word).
//
// Creature IDs: "soweli_0", "akesi_45", "kala_20", "waso_87"
//   = "{category}_{wordIndex}"
//
// AI mapping:
//   soweli, akesi → walk (mouse AI: ground walk, bounce off walls)
//   waso          → fly  (bird AI: horizontal flight, bounce off walls)
//   kala          → swim (fish AI: horizontal swim in water, die in air)
//
// Size: creatures can be up to 7×7 tiles. Default is 1×1.
// Multi-tile creatures use only 'walk' AI (ground) or 'fly'/'swim' AI.
// Body layout: 2D array (rows top-to-bottom, cols left-to-right) facing RIGHT.
//   0 = empty, 1 = full (hittable), 2 = head (breathing + hittable).
//   Mirrored horizontally when facing left.
// Position (x, y) = bottom-left corner of bounding box.
// ============================================================

export const CREATURE_CATEGORIES = ['soweli', 'akesi', 'kala', 'waso', 'pipi'] as const;
export type CreatureCategory = typeof CREATURE_CATEGORIES[number];

/** Word index in the 120-word Toki Pona vocabulary for each category name. */
export const CREATURE_CATEGORY_WORD_INDEX: Record<CreatureCategory, number> = {
    soweli: 98, akesi: 1, kala: 20, waso: 116, pipi: 81,
};

/** All 120 word indices approved per category (600 total creatures). */
const ALL_120 = new Set(Array.from({ length: 120 }, (_, i) => i));
export const APPROVED_CREATURES: Record<CreatureCategory, Set<number>> = {
    soweli: new Set(ALL_120),
    akesi: new Set(ALL_120),
    kala: new Set(ALL_120),
    waso: new Set(ALL_120),
    pipi: new Set(ALL_120),
};

export type CreatureAI = 'walk' | 'stair' | 'cling' | 'fly' | 'swim';

// ── Multi-tile creature size system ──
// Body cell values: 0=empty, 1=full (hittable by sword), 2=head (breathing + hittable).
// Layout is defined facing RIGHT. Mirrored when facing left.
// Rows top-to-bottom, columns left-to-right.

export interface CreatureSize {
    width: number;   // tiles wide (1-7)
    height: number;  // tiles tall (1-7)
    body: number[][]; // [height][width], facing right
}

const DEFAULT_CREATURE_SIZE: CreatureSize = {
    width: 1, height: 1, body: [[2]],
};

/**
 * Per-creature size overrides. Key = creature ID (e.g. "soweli_84").
 * Creatures not listed here default to 1×1.
 */
export const CREATURE_SIZES: Record<string, CreatureSize> = {
    // Capybara (soweli pona) — 2 wide, 1 tall
    'soweli_84': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // Horse (soweli nimi) — 2 wide, 2 tall
    'soweli_66': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Giraffe (soweli sewi) — 2 wide, 6 tall
    'soweli_90': {
        width: 2, height: 6,
        body: [
            [0, 2],
            [0, 1],
            [0, 1],
            [0, 1],
            [1, 1],
            [1, 1],
        ],
    },
    // Elephant (soweli suli) — 4 wide, 3 tall
    'soweli_99': {
        width: 4, height: 3,
        body: [
            [0, 1, 1, 2],
            [1, 1, 1, 1],
            [1, 0, 0, 1],
        ],
    },
    // Blue Whale (kala suli) — 7 wide, 5 tall
    'kala_99': {
        width: 7, height: 5,
        body: [
            [0, 0, 1, 1, 1, 0, 0],
            [0, 1, 1, 1, 1, 1, 0],
            [1, 1, 1, 1, 1, 1, 2],
            [0, 1, 1, 1, 1, 1, 0],
            [0, 0, 1, 1, 1, 0, 0],
        ],
    },
    // Human (soweli mi) — 1 wide, 2 tall
    'soweli_52': {
        width: 1, height: 2,
        body: [
            [2],
            [1],
        ],
    },
    // Mule (soweli anu) — 2 wide, 2 tall
    'soweli_7': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Zebra (soweli sama) — 2 wide, 2 tall
    'soweli_86': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Gorilla (soweli sina) — 2 wide, 2 tall
    'soweli_94': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Giant Panda (soweli taso) — 2 wide, 2 tall
    'soweli_104': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Brown Bear (soweli la) — 2 wide, 2 tall
    'soweli_33': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Rhinoceros (soweli ni) — 3 wide, 2 tall
    'soweli_65': {
        width: 3, height: 2,
        body: [
            [1, 1, 2],
            [1, 0, 1],
        ],
    },
    // Snow Leopard (soweli ona) — 2 wide, 1 tall
    'soweli_70': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // Snake (akesi li) — 2 wide, 1 tall
    'akesi_39': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // King Snake (akesi sama) — 2 wide, 1 tall
    'akesi_86': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // King Cobra (akesi sina) — 1 wide, 2 tall
    'akesi_94': {
        width: 1, height: 2,
        body: [
            [2],
            [1],
        ],
    },
    // Saltwater Crocodile (akesi la) — 3 wide, 1 tall
    'akesi_33': {
        width: 3, height: 1,
        body: [[1, 1, 2]],
    },
    // Anaconda (akesi ona) — 3 wide, 1 tall
    'akesi_70': {
        width: 3, height: 1,
        body: [[1, 1, 2]],
    },
    // Whale (kala a) — 4 wide, 3 tall
    'kala_0': {
        width: 4, height: 3,
        body: [
            [0, 1, 1, 0],
            [1, 1, 1, 2],
            [0, 1, 1, 0],
        ],
    },
    // Lungfish (kala anu) — 2 wide, 1 tall
    'kala_7': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // Beluga (kala sina) — 3 wide, 2 tall
    'kala_94': {
        width: 3, height: 2,
        body: [
            [1, 1, 2],
            [0, 1, 0],
        ],
    },
    // Coelacanth (kala taso) — 2 wide, 1 tall
    'kala_104': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // Shark (kala la) — 2 wide, 1 tall
    'kala_33': {
        width: 2, height: 1,
        body: [[1, 2]],
    },
    // Giant Squid (kala ona) — 3 wide, 3 tall
    'kala_70': {
        width: 3, height: 3,
        body: [
            [0, 2, 0],
            [1, 1, 1],
            [1, 0, 1],
        ],
    },
    // Peacock (waso a) — 2 wide, 2 tall
    'waso_0': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
    // Flamingo (waso ni) — 1 wide, 2 tall
    'waso_65': {
        width: 1, height: 2,
        body: [
            [2],
            [1],
        ],
    },
    // Great White Shark (kala alasa) — 3 wide, 2 tall
    'kala_3': {
        width: 3, height: 2,
        body: [
            [1, 1, 2],
            [0, 1, 0],
        ],
    },
    // Manta Ray (kala lipu) — 3 wide, 2 tall
    'kala_42': {
        width: 3, height: 2,
        body: [
            [1, 2, 1],
            [0, 1, 0],
        ],
    },
    // Dolphin (kala toki) — 3 wide, 1 tall
    'kala_108': {
        width: 3, height: 1,
        body: [[1, 1, 2]],
    },
    // Harpy Eagle (waso alasa) — 2 wide, 2 tall
    'waso_3': {
        width: 2, height: 2,
        body: [
            [1, 2],
            [1, 1],
        ],
    },
};

/** Get the size definition for a creature. Returns 1×1 default for unregistered creatures. */
export function getCreatureSize(creatureType: string): CreatureSize {
    return CREATURE_SIZES[creatureType] || DEFAULT_CREATURE_SIZE;
}

/**
 * Get all world-tile positions occupied by a creature, accounting for facing direction.
 * Position (x, y) = bottom-left of bounding box.
 * Returns array of { tx, ty, cell } where cell is body value (1=full, 2=head).
 */
export function getCreatureBodyTiles(
    x: number, y: number, facing: number, size: CreatureSize,
): { tx: number; ty: number; cell: number }[] {
    const tiles: { tx: number; ty: number; cell: number }[] = [];
    for (let r = 0; r < size.height; r++) {
        for (let c = 0; c < size.width; c++) {
            const col = facing >= 0 ? c : (size.width - 1 - c);
            const cell = size.body[r][col];
            if (cell > 0) {
                tiles.push({
                    tx: x + c,
                    ty: y - size.height + 1 + r,
                    cell,
                });
            }
        }
    }
    return tiles;
}

/** Get the world position of the creature's head tile. Returns null if no head defined. */
export function getCreatureHeadPos(
    x: number, y: number, facing: number, size: CreatureSize,
): { tx: number; ty: number } | null {
    for (let r = 0; r < size.height; r++) {
        for (let c = 0; c < size.width; c++) {
            const col = facing >= 0 ? c : (size.width - 1 - c);
            if (size.body[r][col] === 2) {
                return { tx: x + c, ty: y - size.height + 1 + r };
            }
        }
    }
    return null;
}

/** Build a creature ID string from category + word index. */
export function makeCreatureId(category: CreatureCategory, wordIndex: number): string {
    return `${category}_${wordIndex}`;
}

/** Parse a creature ID string back into category + word index. Returns null for legacy types. */
export function parseCreatureId(id: string): { category: CreatureCategory; wordIndex: number } | null {
    for (const cat of CREATURE_CATEGORIES) {
        if (id.startsWith(cat + '_')) {
            const idx = parseInt(id.slice(cat.length + 1), 10);
            if (!isNaN(idx) && idx >= 0 && idx < 120) return { category: cat, wordIndex: idx };
        }
    }
    return null;
}

/** Determine which AI algorithm a creature type uses.
 *  Multi-tile ground creatures (width>1 or height>1) always use 'walk' AI. */
export function getCreatureAI(type: string): CreatureAI {
    // Multi-tile creatures only support walk/fly/swim — no stair or cling
    const size = getCreatureSize(type);
    const isMultiTile = size.width > 1 || size.height > 1;

    if (!isMultiTile) {
        if (type === RodentType.RAT) return 'stair';
        if (type === RodentType.SQUIRREL) return 'cling';
    }
    const parsed = parseCreatureId(type);
    if (parsed) {
        if (parsed.category === 'pipi') return isMultiTile ? 'walk' : 'cling';
        if (parsed.category === 'waso') return 'fly';
        if (parsed.category === 'kala') return 'swim';
    }
    return 'walk'; // mouse, crab, soweli, akesi, multi-tile ground, and default
}

/** Convert HSL (h: 0-360, s/l: 0-1) to a packed 0xRRGGBB integer. */
function hslToHex(h: number, s: number, l: number): number {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return (Math.round((r + m) * 255) << 16) | (Math.round((g + m) * 255) << 8) | Math.round((b + m) * 255);
}

/** Get the display color for any creature type (legacy or new). */
export function getCreatureColor(type: string): number {
    if (type in RODENT_COLORS) return RODENT_COLORS[type];
    const parsed = parseCreatureId(type);
    if (!parsed) return 0xffffff;
    // Deterministic hue per category, spread by word index
    const baseHue: Record<CreatureCategory, number> = {
        soweli: 30,   // warm browns/oranges for mammals
        akesi: 120,   // greens for reptiles
        kala: 210,    // blues for fish
        waso: 300,    // purples/magentas for birds
        pipi: 60,     // golden/yellow for insects
    };
    const hue = (baseHue[parsed.category] + parsed.wordIndex * 3) % 360;
    return hslToHex(hue, 0.65, 0.50);
}

/**
 * Match a 4-word speech command against the creature spawn pattern:
 * "o pali [category] [modifier]" = [68, 73, categoryIndex, wordIndex].
 * Only returns approved creatures. Returns the creature ID string, or null if no match.
 */
export function matchCreatureSpawn(words: number[]): string | null {
    if (words.length !== 4 || words[0] !== 68 || words[1] !== 73) return null;
    for (const cat of CREATURE_CATEGORIES) {
        if (words[2] === CREATURE_CATEGORY_WORD_INDEX[cat]) {
            const wordIndex = words[3];
            if (wordIndex >= 0 && wordIndex < 120 && APPROVED_CREATURES[cat].has(wordIndex)) {
                return makeCreatureId(cat, wordIndex);
            }
        }
    }
    return null;
}

/** A rodent stored inside a RATS_NEST block, counting down to respawn. */
export interface NestOccupant {
    rodentType: string;
    timer: number;        // counts down from RATS_NEST_TIMER to 0
    facing: number;       // facing when absorbed (toward the nest)
    wallDir?: number;     // squirrel surface state
    vertDir?: number;     // squirrel surface state
}

/** Returned by tickMiceMovement when a rodent is absorbed by a RATS_NEST block. */
export interface NestAbsorption {
    mouseId: number;
    nestX: number;
    nestY: number;
    rodentType: string;
    facing: number;
    wallDir?: number;
    vertDir?: number;
}

/** Returned by WorldData.tickNests when a rodent's timer hits 0. */
export interface NestRespawn {
    nestX: number;
    nestY: number;
    rodentType: string;
    facing: number;       // original facing toward nest; caller should negate for "away"
    wallDir?: number;
    vertDir?: number;
}
export const RODENT_SPAWN_PREFIX = [68, 73, 98]; // o, pali, soweli — shared command prefix
export const MOUSE_SPAWN_PHRASE = [68, 73, 98, 40];    // o pali soweli lili (Mouse)
export const RAT_SPAWN_PHRASE = [68, 73, 98, 47];      // o pali soweli lupa (Rat)
export const SQUIRREL_SPAWN_PHRASE = [68, 73, 98, 1];  // o pali soweli akesi (Squirrel)
export const CRAB_SPAWN_PHRASE = [68, 73, 81, 45];    // o pali pipi luka (Crab)

// ============================================================
// TREE SPAWN COMMAND CONSTANT
// "o kasi" — spawns a tree (trunk + leaf triangle) in front of player
// ============================================================

export const TREE_SPAWN_PHRASE = [68, 23]; // o kasi

// Tree structure: 6 trunk blocks + triangular leaf canopy
// Each layer is { halfWidth, count } — leaves span from (centerX - halfWidth) to (centerX + halfWidth)
// Layers are placed bottom-to-top above the trunk
export const TREE_TRUNK_HEIGHT = 6;
export const TREE_LEAF_LAYERS: { halfWidth: number; rows: number }[] = [
    { halfWidth: 3, rows: 2 },  // 7-wide
    { halfWidth: 2, rows: 2 },  // 5-wide
    { halfWidth: 1, rows: 2 },  // 3-wide
    { halfWidth: 0, rows: 2 },  // 1-wide
];

// ============================================================
// BLOCK SPAWN COMMAND CONSTANTS
// "o pali kiwen <block-name>" — puts a block in the player's hand
// ============================================================

export const BLOCK_SPAWN_COMMANDS: { phrase: number[]; blockType: TileType }[] = [
    { phrase: [68, 73, 27, 27],     blockType: TileType.STONE },  // o pali kiwen kiwen
    { phrase: [68, 73, 27, 48],     blockType: TileType.DIRT },   // o pali kiwen ma
    { phrase: [68, 73, 27, 23],     blockType: TileType.GRASS },  // o pali kiwen kasi
    { phrase: [68, 73, 27, 36],     blockType: TileType.SKULL },  // o pali kiwen lawa
    { phrase: [68, 73, 27, 90],     blockType: TileType.SKY },    // o pali kiwen sewi
    { phrase: [68, 73, 26, 23],     blockType: TileType.HEART_CONTAINER }, // o pali kili kasi
    { phrase: [68, 73, 27, 78, 99], blockType: TileType.BIG_HEART_CONTAINER }, // o pali kiwen pilin suli
    { phrase: [68, 73, 10, 74],     blockType: TileType.LADDER }, // o pali en palisa
    { phrase: [68, 73, 74, 23],     blockType: TileType.STICK },  // o pali palisa kasi
    { phrase: [68, 73, 23, 42],     blockType: TileType.LEAVES }, // o pali kasi lipu
    { phrase: [68, 73, 23, 74],     blockType: TileType.TRUNK },  // o pali kasi palisa
    { phrase: [68, 73, 23, 15],     blockType: TileType.LOG },    // o pali kasi insa
    { phrase: [68, 73, 27, 14],     blockType: TileType.FLINT }, // o pali kiwen ilo
    { phrase: [68, 73, 23, 101],    blockType: TileType.PLANK },     // o pali kasi supa
    { phrase: [68, 73, 27, 15],     blockType: TileType.AMETHYST },  // o pali kiwen insa
    { phrase: [68, 73, 27, 30],     blockType: TileType.OPAL },      // o pali kiwen kule
    { phrase: [68, 73, 27, 35],     blockType: TileType.TURQUOISE }, // o pali kiwen laso
    { phrase: [68, 73, 27, 47],     blockType: TileType.PORTAL },    // o pali kiwen lupa
    { phrase: [68, 73, 27, 71],     blockType: TileType.RUNE },      // o pali kiwen open
    { phrase: [68, 73, 47, 98],     blockType: TileType.RATS_NEST }, // o pali lupa soweli
    { phrase: [68, 73, 8, 23],      blockType: TileType.WOODEN_SHIELD }, // o pali awen kasi
    { phrase: [68, 73, 74, 27],     blockType: TileType.STONE_CLUB },    // o pali palisa kiwen
    { phrase: [68, 73, 74, 87],     blockType: TileType.IRON_SWORD },    // o pali palisa seli
    { phrase: [68, 73, 27, 78],     blockType: TileType.RED_STONE },     // o pali kiwen pilin
    { phrase: [68, 73, 23, 40],     blockType: TileType.SAPLING },       // o pali kasi lili
];

// ============================================================
// TILE → DICTIONARY KEY MAPPING
// Maps each TileType to [wordIndexA, wordIndexB] for Toki Pona dictionary lookup
// Word indices correspond to TokiPonaData WORD_LIST order
// ============================================================

export const TILE_DICTIONARY_MAP: Partial<Record<TileType, [number, number]>> = {
    [TileType.SKY]: [27, 90],               // kiwen sewi
    [TileType.DIRT]: [27, 48],              // kiwen ma
    [TileType.STONE]: [27, 27],             // kiwen kiwen
    [TileType.GRASS]: [27, 23],             // kiwen kasi
    [TileType.RUNE]: [27, 71],              // kiwen open
    [TileType.PORTAL]: [27, 47],            // kiwen lupa
    [TileType.SKULL]: [27, 36],             // kiwen lawa
    [TileType.HEART_CONTAINER]: [26, 23],   // kili kasi
    [TileType.BIG_HEART_CONTAINER]: [78, 99], // pilin suli
    [TileType.LADDER]: [10, 74],            // en palisa
    [TileType.STICK]: [74, 23],             // palisa kasi
    [TileType.LEAVES]: [23, 42],            // kasi lipu
    [TileType.TRUNK]: [23, 74],             // kasi palisa
    [TileType.LOG]: [23, 15],               // kasi insa
    [TileType.FLINT]: [27, 14],             // kiwen ilo
    [TileType.PLANK]: [23, 101],            // kasi supa
    [TileType.AMETHYST]: [27, 15],          // kiwen insa
    [TileType.OPAL]: [27, 30],              // kiwen kule
    [TileType.TURQUOISE]: [27, 35],         // kiwen laso
    [TileType.RATS_NEST]: [47, 98],          // lupa soweli
    [TileType.WATER]: [106, 106],             // telo telo
    [TileType.WATER_SURFACE]: [106, 106],     // telo telo (surface)
    [TileType.WOODEN_SHIELD]: [8, 23],        // awen kasi
    [TileType.STONE_CLUB]: [74, 27],          // palisa kiwen
    [TileType.IRON_SWORD]: [74, 87],          // palisa seli
    [TileType.RED_STONE]: [27, 78],            // kiwen pilin
    [TileType.BOOTS]: [67, 90],               // noka sewi
    [TileType.YELLOW_DOOR]: [47, 18],         // lupa jelo
    [TileType.YELLOW_DOOR_OPEN]: [47, 18],    // lupa jelo (opened)
    [TileType.YELLOW_KEY]: [14, 18],          // ilo jelo
    [TileType.SAPLING]: [23, 40],             // kasi lili
};

// ============================================================
// PLACEHOLDER TILES — command-spawned only, data-driven
// Solid blocks with category-colored backgrounds + glyph overlays
// ============================================================

export interface PlaceholderTileInfo {
    type: TileType;
    word1: number;
    word2: number;
    color: number;
}

export const PLACEHOLDER_TILES: PlaceholderTileInfo[] = [
    // ilo (tools) — copper-tan 0xC4956A
    { type: TileType.ILO_A, word1: 14, word2: 0, color: 0xC4956A },
    { type: TileType.ILO_AKESI, word1: 14, word2: 1, color: 0xC4956A },
    { type: TileType.ILO_ALA, word1: 14, word2: 2, color: 0xC4956A },
    { type: TileType.ILO_ALASA, word1: 14, word2: 3, color: 0xC4956A },
    { type: TileType.ILO_ALE, word1: 14, word2: 4, color: 0xC4956A },
    { type: TileType.ILO_ANPA, word1: 14, word2: 5, color: 0xC4956A },
    { type: TileType.ILO_ANU, word1: 14, word2: 7, color: 0xC4956A },
    { type: TileType.ILO_AWEN, word1: 14, word2: 8, color: 0xC4956A },
    { type: TileType.ILO_EN, word1: 14, word2: 10, color: 0xC4956A },
    { type: TileType.ILO_ESUN, word1: 14, word2: 11, color: 0xC4956A },
    { type: TileType.ILO_IJO, word1: 14, word2: 12, color: 0xC4956A },
    { type: TileType.ILO_ILO, word1: 14, word2: 14, color: 0xC4956A },
    { type: TileType.ILO_JAKI, word1: 14, word2: 16, color: 0xC4956A },
    { type: TileType.ILO_JAN, word1: 14, word2: 17, color: 0xC4956A },
    { type: TileType.ILO_JO, word1: 14, word2: 19, color: 0xC4956A },
    { type: TileType.ILO_KALA, word1: 14, word2: 20, color: 0xC4956A },
    { type: TileType.ILO_KALAMA, word1: 14, word2: 21, color: 0xC4956A },
    { type: TileType.ILO_KAMA, word1: 14, word2: 22, color: 0xC4956A },
    { type: TileType.ILO_KASI, word1: 14, word2: 23, color: 0xC4956A },
    { type: TileType.ILO_KEN, word1: 14, word2: 24, color: 0xC4956A },
    { type: TileType.ILO_KEPEKEN, word1: 14, word2: 25, color: 0xC4956A },
    { type: TileType.ILO_KILI, word1: 14, word2: 26, color: 0xC4956A },
    { type: TileType.ILO_KIWEN, word1: 14, word2: 27, color: 0xC4956A },
    { type: TileType.ILO_KON, word1: 14, word2: 29, color: 0xC4956A },
    { type: TileType.ILO_KULE, word1: 14, word2: 30, color: 0xC4956A },
    { type: TileType.ILO_KUTE, word1: 14, word2: 32, color: 0xC4956A },
    { type: TileType.ILO_LA, word1: 14, word2: 33, color: 0xC4956A },
    { type: TileType.ILO_LAPE, word1: 14, word2: 34, color: 0xC4956A },
    { type: TileType.ILO_LEN, word1: 14, word2: 37, color: 0xC4956A },
    { type: TileType.ILO_LETE, word1: 14, word2: 38, color: 0xC4956A },
    { type: TileType.ILO_LI, word1: 14, word2: 39, color: 0xC4956A },
    { type: TileType.ILO_LILI, word1: 14, word2: 40, color: 0xC4956A },
    { type: TileType.ILO_LIPU, word1: 14, word2: 42, color: 0xC4956A },
    { type: TileType.ILO_LOJE, word1: 14, word2: 43, color: 0xC4956A },
    { type: TileType.ILO_LON, word1: 14, word2: 44, color: 0xC4956A },
    { type: TileType.ILO_LUKA, word1: 14, word2: 45, color: 0xC4956A },
    { type: TileType.ILO_LUKIN, word1: 14, word2: 46, color: 0xC4956A },
    { type: TileType.ILO_LUPA, word1: 14, word2: 47, color: 0xC4956A },
    { type: TileType.ILO_MA, word1: 14, word2: 48, color: 0xC4956A },
    { type: TileType.ILO_MAMA, word1: 14, word2: 49, color: 0xC4956A },
    { type: TileType.ILO_MANI, word1: 14, word2: 50, color: 0xC4956A },
    { type: TileType.ILO_MIJE, word1: 14, word2: 53, color: 0xC4956A },
    { type: TileType.ILO_MOKU, word1: 14, word2: 54, color: 0xC4956A },
    { type: TileType.ILO_MOLI, word1: 14, word2: 55, color: 0xC4956A },
    { type: TileType.ILO_MU, word1: 14, word2: 57, color: 0xC4956A },
    { type: TileType.ILO_MUN, word1: 14, word2: 58, color: 0xC4956A },
    { type: TileType.ILO_NASIN, word1: 14, word2: 63, color: 0xC4956A },
    { type: TileType.ILO_NIMI, word1: 14, word2: 66, color: 0xC4956A },
    { type: TileType.ILO_NOKA, word1: 14, word2: 67, color: 0xC4956A },
    { type: TileType.ILO_O, word1: 14, word2: 68, color: 0xC4956A },
    { type: TileType.ILO_OLIN, word1: 14, word2: 69, color: 0xC4956A },
    { type: TileType.ILO_OPEN, word1: 14, word2: 71, color: 0xC4956A },
    { type: TileType.ILO_PALISA, word1: 14, word2: 74, color: 0xC4956A },
    { type: TileType.ILO_PAN, word1: 14, word2: 75, color: 0xC4956A },
    { type: TileType.ILO_PILIN, word1: 14, word2: 78, color: 0xC4956A },
    { type: TileType.ILO_PIPI, word1: 14, word2: 81, color: 0xC4956A },
    { type: TileType.ILO_POKA, word1: 14, word2: 82, color: 0xC4956A },
    { type: TileType.ILO_POKI, word1: 14, word2: 83, color: 0xC4956A },
    { type: TileType.ILO_SELI, word1: 14, word2: 87, color: 0xC4956A },
    { type: TileType.ILO_SELO, word1: 14, word2: 88, color: 0xC4956A },
    { type: TileType.ILO_SEME, word1: 14, word2: 89, color: 0xC4956A },
    { type: TileType.ILO_SIKE, word1: 14, word2: 92, color: 0xC4956A },
    { type: TileType.ILO_SITELEN, word1: 14, word2: 96, color: 0xC4956A },
    { type: TileType.ILO_SOWELI, word1: 14, word2: 98, color: 0xC4956A },
    { type: TileType.ILO_SUNO, word1: 14, word2: 100, color: 0xC4956A },
    { type: TileType.ILO_TENPO, word1: 14, word2: 107, color: 0xC4956A },
    { type: TileType.ILO_TOKI, word1: 14, word2: 108, color: 0xC4956A },
    { type: TileType.ILO_TU, word1: 14, word2: 110, color: 0xC4956A },
    { type: TileType.ILO_UTA, word1: 14, word2: 112, color: 0xC4956A },
    { type: TileType.ILO_UTALA, word1: 14, word2: 113, color: 0xC4956A },
    { type: TileType.ILO_WAN, word1: 14, word2: 115, color: 0xC4956A },
    { type: TileType.ILO_WASO, word1: 14, word2: 116, color: 0xC4956A },
    // kasi (plants) — forest green 0x3B7A3B
    { type: TileType.KASI_AKESI, word1: 23, word2: 1, color: 0x3B7A3B },
    { type: TileType.KASI_ALE, word1: 23, word2: 4, color: 0x3B7A3B },
    { type: TileType.KASI_ANU, word1: 23, word2: 7, color: 0x3B7A3B },
    { type: TileType.KASI_EN, word1: 23, word2: 10, color: 0x3B7A3B },
    { type: TileType.KASI_IJO, word1: 23, word2: 12, color: 0x3B7A3B },
    { type: TileType.KASI_JAN, word1: 23, word2: 17, color: 0x3B7A3B },
    { type: TileType.KASI_JELO, word1: 23, word2: 18, color: 0x3B7A3B },
    { type: TileType.KASI_KAMA, word1: 23, word2: 22, color: 0x3B7A3B },
    { type: TileType.KASI_KEPEKEN, word1: 23, word2: 25, color: 0x3B7A3B },
    { type: TileType.KASI_KILI, word1: 23, word2: 26, color: 0x3B7A3B },
    { type: TileType.KASI_KIWEN, word1: 23, word2: 27, color: 0x3B7A3B },
    { type: TileType.KASI_LASO, word1: 23, word2: 35, color: 0x3B7A3B },
    { type: TileType.KASI_LAWA, word1: 23, word2: 36, color: 0x3B7A3B },
    { type: TileType.KASI_LI, word1: 23, word2: 39, color: 0x3B7A3B },
    { type: TileType.KASI_LINJA, word1: 23, word2: 41, color: 0x3B7A3B },
    { type: TileType.KASI_LUKA, word1: 23, word2: 45, color: 0x3B7A3B },
    { type: TileType.KASI_MA, word1: 23, word2: 48, color: 0x3B7A3B },
    { type: TileType.KASI_MANI, word1: 23, word2: 50, color: 0x3B7A3B },
    { type: TileType.KASI_MELI, word1: 23, word2: 51, color: 0x3B7A3B },
    { type: TileType.KASI_MI, word1: 23, word2: 52, color: 0x3B7A3B },
    { type: TileType.KASI_MOLI, word1: 23, word2: 55, color: 0x3B7A3B },
    { type: TileType.KASI_MONSI, word1: 23, word2: 56, color: 0x3B7A3B },
    { type: TileType.KASI_MU, word1: 23, word2: 57, color: 0x3B7A3B },
    { type: TileType.KASI_MUN, word1: 23, word2: 58, color: 0x3B7A3B },
    { type: TileType.KASI_MUTE, word1: 23, word2: 60, color: 0x3B7A3B },
    { type: TileType.KASI_NOKA, word1: 23, word2: 67, color: 0x3B7A3B },
    { type: TileType.KASI_O, word1: 23, word2: 68, color: 0x3B7A3B },
    { type: TileType.KASI_OLIN, word1: 23, word2: 69, color: 0x3B7A3B },
    { type: TileType.KASI_PAKALA, word1: 23, word2: 72, color: 0x3B7A3B },
    { type: TileType.KASI_PAN, word1: 23, word2: 75, color: 0x3B7A3B },
    { type: TileType.KASI_PANA, word1: 23, word2: 76, color: 0x3B7A3B },
    { type: TileType.KASI_PIMEJA, word1: 23, word2: 79, color: 0x3B7A3B },
    { type: TileType.KASI_PIPI, word1: 23, word2: 81, color: 0x3B7A3B },
    { type: TileType.KASI_POKI, word1: 23, word2: 83, color: 0x3B7A3B },
    { type: TileType.KASI_SELI, word1: 23, word2: 87, color: 0x3B7A3B },
    { type: TileType.KASI_SEME, word1: 23, word2: 89, color: 0x3B7A3B },
    { type: TileType.KASI_SIKE, word1: 23, word2: 92, color: 0x3B7A3B },
    { type: TileType.KASI_SUNO, word1: 23, word2: 100, color: 0x3B7A3B },
    { type: TileType.KASI_SUWI, word1: 23, word2: 102, color: 0x3B7A3B },
    { type: TileType.KASI_TAWA, word1: 23, word2: 105, color: 0x3B7A3B },
    { type: TileType.KASI_TELO, word1: 23, word2: 106, color: 0x3B7A3B },
    { type: TileType.KASI_TOMO, word1: 23, word2: 109, color: 0x3B7A3B },
    { type: TileType.KASI_UTA, word1: 23, word2: 112, color: 0x3B7A3B },
    { type: TileType.KASI_UTALA, word1: 23, word2: 113, color: 0x3B7A3B },
    { type: TileType.KASI_WASO, word1: 23, word2: 116, color: 0x3B7A3B },
    { type: TileType.KASI_WAWA, word1: 23, word2: 117, color: 0x3B7A3B },
    // kasi (plants) — expanded set
    { type: TileType.KASI_A, word1: 23, word2: 0, color: 0x3B7A3B },
    { type: TileType.KASI_ALA, word1: 23, word2: 2, color: 0x3B7A3B },
    { type: TileType.KASI_ALASA, word1: 23, word2: 3, color: 0x3B7A3B },
    { type: TileType.KASI_ANPA, word1: 23, word2: 5, color: 0x3B7A3B },
    { type: TileType.KASI_ANTE, word1: 23, word2: 6, color: 0x3B7A3B },
    { type: TileType.KASI_AWEN, word1: 23, word2: 8, color: 0x3B7A3B },
    { type: TileType.KASI_E, word1: 23, word2: 9, color: 0x3B7A3B },
    { type: TileType.KASI_ESUN, word1: 23, word2: 11, color: 0x3B7A3B },
    { type: TileType.KASI_IKE, word1: 23, word2: 13, color: 0x3B7A3B },
    { type: TileType.KASI_ILO, word1: 23, word2: 14, color: 0x3B7A3B },
    // kasi insa (word2: 15) — covered by LOG
    { type: TileType.KASI_JAKI, word1: 23, word2: 16, color: 0x3B7A3B },
    { type: TileType.KASI_JO, word1: 23, word2: 19, color: 0x3B7A3B },
    { type: TileType.KASI_KALA, word1: 23, word2: 20, color: 0x3B7A3B },
    { type: TileType.KASI_KALAMA, word1: 23, word2: 21, color: 0x3B7A3B },
    { type: TileType.KASI_KASI, word1: 23, word2: 23, color: 0x3B7A3B },
    { type: TileType.KASI_KEN, word1: 23, word2: 24, color: 0x3B7A3B },
    { type: TileType.KASI_KO, word1: 23, word2: 28, color: 0x3B7A3B },
    { type: TileType.KASI_KON, word1: 23, word2: 29, color: 0x3B7A3B },
    { type: TileType.KASI_KULE, word1: 23, word2: 30, color: 0x3B7A3B },
    { type: TileType.KASI_KULUPU, word1: 23, word2: 31, color: 0x3B7A3B },
    { type: TileType.KASI_KUTE, word1: 23, word2: 32, color: 0x3B7A3B },
    { type: TileType.KASI_LA, word1: 23, word2: 33, color: 0x3B7A3B },
    { type: TileType.KASI_LAPE, word1: 23, word2: 34, color: 0x3B7A3B },
    { type: TileType.KASI_LEN, word1: 23, word2: 37, color: 0x3B7A3B },
    { type: TileType.KASI_LETE, word1: 23, word2: 38, color: 0x3B7A3B },
    // kasi lili (word2: 40) — covered by SAPLING
    // kasi lipu (word2: 42) — covered by LEAVES
    { type: TileType.KASI_LOJE, word1: 23, word2: 43, color: 0x3B7A3B },
    { type: TileType.KASI_LON, word1: 23, word2: 44, color: 0x3B7A3B },
    { type: TileType.KASI_LUKIN, word1: 23, word2: 46, color: 0x3B7A3B },
    { type: TileType.KASI_LUPA, word1: 23, word2: 47, color: 0x3B7A3B },
    { type: TileType.KASI_MAMA, word1: 23, word2: 49, color: 0x3B7A3B },
    { type: TileType.KASI_MIJE, word1: 23, word2: 53, color: 0x3B7A3B },
    { type: TileType.KASI_MOKU, word1: 23, word2: 54, color: 0x3B7A3B },
    { type: TileType.KASI_MUSI, word1: 23, word2: 59, color: 0x3B7A3B },
    { type: TileType.KASI_NANPA, word1: 23, word2: 61, color: 0x3B7A3B },
    { type: TileType.KASI_NASA, word1: 23, word2: 62, color: 0x3B7A3B },
    { type: TileType.KASI_NASIN, word1: 23, word2: 63, color: 0x3B7A3B },
    { type: TileType.KASI_NENA, word1: 23, word2: 64, color: 0x3B7A3B },
    { type: TileType.KASI_NI, word1: 23, word2: 65, color: 0x3B7A3B },
    { type: TileType.KASI_NIMI, word1: 23, word2: 66, color: 0x3B7A3B },
    { type: TileType.KASI_ONA, word1: 23, word2: 70, color: 0x3B7A3B },
    { type: TileType.KASI_OPEN, word1: 23, word2: 71, color: 0x3B7A3B },
    { type: TileType.KASI_PALI, word1: 23, word2: 73, color: 0x3B7A3B },
    // kasi palisa (word2: 74) — covered by TRUNK
    { type: TileType.KASI_PI, word1: 23, word2: 77, color: 0x3B7A3B },
    { type: TileType.KASI_PILIN, word1: 23, word2: 78, color: 0x3B7A3B },
    { type: TileType.KASI_PINI, word1: 23, word2: 80, color: 0x3B7A3B },
    { type: TileType.KASI_POKA, word1: 23, word2: 82, color: 0x3B7A3B },
    { type: TileType.KASI_PONA, word1: 23, word2: 84, color: 0x3B7A3B },
    { type: TileType.KASI_PU, word1: 23, word2: 85, color: 0x3B7A3B },
    { type: TileType.KASI_SAMA, word1: 23, word2: 86, color: 0x3B7A3B },
    { type: TileType.KASI_SELO, word1: 23, word2: 88, color: 0x3B7A3B },
    { type: TileType.KASI_SEWI, word1: 23, word2: 90, color: 0x3B7A3B },
    { type: TileType.KASI_SIJELO, word1: 23, word2: 91, color: 0x3B7A3B },
    { type: TileType.KASI_SIN, word1: 23, word2: 93, color: 0x3B7A3B },
    { type: TileType.KASI_SINA, word1: 23, word2: 94, color: 0x3B7A3B },
    { type: TileType.KASI_SINPIN, word1: 23, word2: 95, color: 0x3B7A3B },
    { type: TileType.KASI_SITELEN, word1: 23, word2: 96, color: 0x3B7A3B },
    { type: TileType.KASI_SONA, word1: 23, word2: 97, color: 0x3B7A3B },
    { type: TileType.KASI_SOWELI, word1: 23, word2: 98, color: 0x3B7A3B },
    { type: TileType.KASI_SULI, word1: 23, word2: 99, color: 0x3B7A3B },
    // kasi supa (word2: 101) — covered by PLANK
    { type: TileType.KASI_TAN, word1: 23, word2: 103, color: 0x3B7A3B },
    { type: TileType.KASI_TASO, word1: 23, word2: 104, color: 0x3B7A3B },
    { type: TileType.KASI_TENPO, word1: 23, word2: 107, color: 0x3B7A3B },
    { type: TileType.KASI_TOKI, word1: 23, word2: 108, color: 0x3B7A3B },
    { type: TileType.KASI_TU, word1: 23, word2: 110, color: 0x3B7A3B },
    { type: TileType.KASI_UNPA, word1: 23, word2: 111, color: 0x3B7A3B },
    { type: TileType.KASI_WALO, word1: 23, word2: 114, color: 0x3B7A3B },
    { type: TileType.KASI_WAN, word1: 23, word2: 115, color: 0x3B7A3B },
    { type: TileType.KASI_WEKA, word1: 23, word2: 118, color: 0x3B7A3B },
    { type: TileType.KASI_WILE, word1: 23, word2: 119, color: 0x3B7A3B },
    // kili (fruits) — muted red 0xC46B6B
    { type: TileType.KILI_AKESI, word1: 26, word2: 1, color: 0xC46B6B },
    { type: TileType.KILI_ALE, word1: 26, word2: 4, color: 0xC46B6B },
    { type: TileType.KILI_ANPA, word1: 26, word2: 5, color: 0xC46B6B },
    { type: TileType.KILI_ESUN, word1: 26, word2: 11, color: 0xC46B6B },
    { type: TileType.KILI_IJO, word1: 26, word2: 12, color: 0xC46B6B },
    { type: TileType.KILI_IKE, word1: 26, word2: 13, color: 0xC46B6B },
    { type: TileType.KILI_INSA, word1: 26, word2: 15, color: 0xC46B6B },
    { type: TileType.KILI_JAKI, word1: 26, word2: 16, color: 0xC46B6B },
    { type: TileType.KILI_JELO, word1: 26, word2: 18, color: 0xC46B6B },
    { type: TileType.KILI_KEN, word1: 26, word2: 24, color: 0xC46B6B },
    { type: TileType.KILI_KILI, word1: 26, word2: 26, color: 0xC46B6B },
    { type: TileType.KILI_KIWEN, word1: 26, word2: 27, color: 0xC46B6B },
    { type: TileType.KILI_KULUPU, word1: 26, word2: 31, color: 0xC46B6B },
    { type: TileType.KILI_LASO, word1: 26, word2: 35, color: 0xC46B6B },
    { type: TileType.KILI_LAWA, word1: 26, word2: 36, color: 0xC46B6B },
    { type: TileType.KILI_LETE, word1: 26, word2: 38, color: 0xC46B6B },
    { type: TileType.KILI_LINJA, word1: 26, word2: 41, color: 0xC46B6B },
    { type: TileType.KILI_LOJE, word1: 26, word2: 43, color: 0xC46B6B },
    { type: TileType.KILI_MA, word1: 26, word2: 48, color: 0xC46B6B },
    { type: TileType.KILI_MOLI, word1: 26, word2: 55, color: 0xC46B6B },
    { type: TileType.KILI_MUSI, word1: 26, word2: 59, color: 0xC46B6B },
    { type: TileType.KILI_MUTE, word1: 26, word2: 60, color: 0xC46B6B },
    { type: TileType.KILI_NANPA, word1: 26, word2: 61, color: 0xC46B6B },
    { type: TileType.KILI_OPEN, word1: 26, word2: 71, color: 0xC46B6B },
    { type: TileType.KILI_PAKALA, word1: 26, word2: 72, color: 0xC46B6B },
    { type: TileType.KILI_PALISA, word1: 26, word2: 74, color: 0xC46B6B },
    { type: TileType.KILI_PAN, word1: 26, word2: 75, color: 0xC46B6B },
    { type: TileType.KILI_PU, word1: 26, word2: 85, color: 0xC46B6B },
    { type: TileType.KILI_SELI, word1: 26, word2: 87, color: 0xC46B6B },
    { type: TileType.KILI_SELO, word1: 26, word2: 88, color: 0xC46B6B },
    { type: TileType.KILI_SONA, word1: 26, word2: 97, color: 0xC46B6B },
    { type: TileType.KILI_SOWELI, word1: 26, word2: 98, color: 0xC46B6B },
    { type: TileType.KILI_SUWI, word1: 26, word2: 102, color: 0xC46B6B },
    { type: TileType.KILI_TELO, word1: 26, word2: 106, color: 0xC46B6B },
    { type: TileType.KILI_UNPA, word1: 26, word2: 111, color: 0xC46B6B },
    { type: TileType.KILI_WILE, word1: 26, word2: 119, color: 0xC46B6B },
    // kili (fruits) — expanded set, same muted red 0xC46B6B
    { type: TileType.KILI_A, word1: 26, word2: 0, color: 0xC46B6B },
    { type: TileType.KILI_ALA, word1: 26, word2: 2, color: 0xC46B6B },
    { type: TileType.KILI_ALASA, word1: 26, word2: 3, color: 0xC46B6B },
    { type: TileType.KILI_ANTE, word1: 26, word2: 6, color: 0xC46B6B },
    { type: TileType.KILI_ANU, word1: 26, word2: 7, color: 0xC46B6B },
    { type: TileType.KILI_AWEN, word1: 26, word2: 8, color: 0xC46B6B },
    { type: TileType.KILI_E, word1: 26, word2: 9, color: 0xC46B6B },
    { type: TileType.KILI_EN, word1: 26, word2: 10, color: 0xC46B6B },
    { type: TileType.KILI_ILO, word1: 26, word2: 14, color: 0xC46B6B },
    { type: TileType.KILI_JAN, word1: 26, word2: 17, color: 0xC46B6B },
    { type: TileType.KILI_JO, word1: 26, word2: 19, color: 0xC46B6B },
    { type: TileType.KILI_KALA, word1: 26, word2: 20, color: 0xC46B6B },
    { type: TileType.KILI_KALAMA, word1: 26, word2: 21, color: 0xC46B6B },
    { type: TileType.KILI_KAMA, word1: 26, word2: 22, color: 0xC46B6B },
    // kili kasi (word2: 23) — covered by HEART_CONTAINER
    { type: TileType.KILI_KEPEKEN, word1: 26, word2: 25, color: 0xC46B6B },
    { type: TileType.KILI_KO, word1: 26, word2: 28, color: 0xC46B6B },
    { type: TileType.KILI_KON, word1: 26, word2: 29, color: 0xC46B6B },
    { type: TileType.KILI_KULE, word1: 26, word2: 30, color: 0xC46B6B },
    { type: TileType.KILI_KUTE, word1: 26, word2: 32, color: 0xC46B6B },
    { type: TileType.KILI_LA, word1: 26, word2: 33, color: 0xC46B6B },
    { type: TileType.KILI_LAPE, word1: 26, word2: 34, color: 0xC46B6B },
    { type: TileType.KILI_LEN, word1: 26, word2: 37, color: 0xC46B6B },
    { type: TileType.KILI_LI, word1: 26, word2: 39, color: 0xC46B6B },
    { type: TileType.KILI_LILI, word1: 26, word2: 40, color: 0xC46B6B },
    { type: TileType.KILI_LIPU, word1: 26, word2: 42, color: 0xC46B6B },
    { type: TileType.KILI_LON, word1: 26, word2: 44, color: 0xC46B6B },
    { type: TileType.KILI_LUKA, word1: 26, word2: 45, color: 0xC46B6B },
    { type: TileType.KILI_LUKIN, word1: 26, word2: 46, color: 0xC46B6B },
    { type: TileType.KILI_LUPA, word1: 26, word2: 47, color: 0xC46B6B },
    { type: TileType.KILI_MAMA, word1: 26, word2: 49, color: 0xC46B6B },
    { type: TileType.KILI_MANI, word1: 26, word2: 50, color: 0xC46B6B },
    { type: TileType.KILI_MELI, word1: 26, word2: 51, color: 0xC46B6B },
    { type: TileType.KILI_MI, word1: 26, word2: 52, color: 0xC46B6B },
    { type: TileType.KILI_MIJE, word1: 26, word2: 53, color: 0xC46B6B },
    { type: TileType.KILI_MOKU, word1: 26, word2: 54, color: 0xC46B6B },
    { type: TileType.KILI_MONSI, word1: 26, word2: 56, color: 0xC46B6B },
    { type: TileType.KILI_MU, word1: 26, word2: 57, color: 0xC46B6B },
    { type: TileType.KILI_MUN, word1: 26, word2: 58, color: 0xC46B6B },
    { type: TileType.KILI_NASA, word1: 26, word2: 62, color: 0xC46B6B },
    { type: TileType.KILI_NASIN, word1: 26, word2: 63, color: 0xC46B6B },
    { type: TileType.KILI_NENA, word1: 26, word2: 64, color: 0xC46B6B },
    { type: TileType.KILI_NI, word1: 26, word2: 65, color: 0xC46B6B },
    { type: TileType.KILI_NIMI, word1: 26, word2: 66, color: 0xC46B6B },
    { type: TileType.KILI_NOKA, word1: 26, word2: 67, color: 0xC46B6B },
    { type: TileType.KILI_O, word1: 26, word2: 68, color: 0xC46B6B },
    { type: TileType.KILI_OLIN, word1: 26, word2: 69, color: 0xC46B6B },
    { type: TileType.KILI_ONA, word1: 26, word2: 70, color: 0xC46B6B },
    { type: TileType.KILI_PALI, word1: 26, word2: 73, color: 0xC46B6B },
    { type: TileType.KILI_PANA, word1: 26, word2: 76, color: 0xC46B6B },
    { type: TileType.KILI_PI, word1: 26, word2: 77, color: 0xC46B6B },
    { type: TileType.KILI_PILIN, word1: 26, word2: 78, color: 0xC46B6B },
    { type: TileType.KILI_PIMEJA, word1: 26, word2: 79, color: 0xC46B6B },
    { type: TileType.KILI_PINI, word1: 26, word2: 80, color: 0xC46B6B },
    { type: TileType.KILI_PIPI, word1: 26, word2: 81, color: 0xC46B6B },
    { type: TileType.KILI_POKA, word1: 26, word2: 82, color: 0xC46B6B },
    { type: TileType.KILI_POKI, word1: 26, word2: 83, color: 0xC46B6B },
    { type: TileType.KILI_PONA, word1: 26, word2: 84, color: 0xC46B6B },
    { type: TileType.KILI_SAMA, word1: 26, word2: 86, color: 0xC46B6B },
    { type: TileType.KILI_SEME, word1: 26, word2: 89, color: 0xC46B6B },
    { type: TileType.KILI_SEWI, word1: 26, word2: 90, color: 0xC46B6B },
    { type: TileType.KILI_SIJELO, word1: 26, word2: 91, color: 0xC46B6B },
    { type: TileType.KILI_SIKE, word1: 26, word2: 92, color: 0xC46B6B },
    { type: TileType.KILI_SIN, word1: 26, word2: 93, color: 0xC46B6B },
    { type: TileType.KILI_SINA, word1: 26, word2: 94, color: 0xC46B6B },
    { type: TileType.KILI_SINPIN, word1: 26, word2: 95, color: 0xC46B6B },
    { type: TileType.KILI_SITELEN, word1: 26, word2: 96, color: 0xC46B6B },
    { type: TileType.KILI_SULI, word1: 26, word2: 99, color: 0xC46B6B },
    { type: TileType.KILI_SUNO, word1: 26, word2: 100, color: 0xC46B6B },
    { type: TileType.KILI_SUPA, word1: 26, word2: 101, color: 0xC46B6B },
    { type: TileType.KILI_TAN, word1: 26, word2: 103, color: 0xC46B6B },
    { type: TileType.KILI_TASO, word1: 26, word2: 104, color: 0xC46B6B },
    { type: TileType.KILI_TAWA, word1: 26, word2: 105, color: 0xC46B6B },
    { type: TileType.KILI_TENPO, word1: 26, word2: 107, color: 0xC46B6B },
    { type: TileType.KILI_TOKI, word1: 26, word2: 108, color: 0xC46B6B },
    { type: TileType.KILI_TOMO, word1: 26, word2: 109, color: 0xC46B6B },
    { type: TileType.KILI_TU, word1: 26, word2: 110, color: 0xC46B6B },
    { type: TileType.KILI_UTA, word1: 26, word2: 112, color: 0xC46B6B },
    { type: TileType.KILI_UTALA, word1: 26, word2: 113, color: 0xC46B6B },
    { type: TileType.KILI_WALO, word1: 26, word2: 114, color: 0xC46B6B },
    { type: TileType.KILI_WAN, word1: 26, word2: 115, color: 0xC46B6B },
    { type: TileType.KILI_WASO, word1: 26, word2: 116, color: 0xC46B6B },
    { type: TileType.KILI_WAWA, word1: 26, word2: 117, color: 0xC46B6B },
    { type: TileType.KILI_WEKA, word1: 26, word2: 118, color: 0xC46B6B },
    // kiwen (blocks) — blue-grey 0x7B8B9E
    { type: TileType.KIWEN_ANPA, word1: 27, word2: 5, color: 0x7B8B9E },
    { type: TileType.KIWEN_IJO, word1: 27, word2: 12, color: 0x7B8B9E },
    { type: TileType.KIWEN_JAN, word1: 27, word2: 17, color: 0x7B8B9E },
    { type: TileType.KIWEN_JELO, word1: 27, word2: 18, color: 0x7B8B9E },
    { type: TileType.KIWEN_JO, word1: 27, word2: 19, color: 0x7B8B9E },
    { type: TileType.KIWEN_KALA, word1: 27, word2: 20, color: 0x7B8B9E },
    { type: TileType.KIWEN_KALAMA, word1: 27, word2: 21, color: 0x7B8B9E },
    { type: TileType.KIWEN_KAMA, word1: 27, word2: 22, color: 0x7B8B9E },
    { type: TileType.KIWEN_KO, word1: 27, word2: 28, color: 0x7B8B9E },
    { type: TileType.KIWEN_KULUPU, word1: 27, word2: 31, color: 0x7B8B9E },
    { type: TileType.KIWEN_KUTE, word1: 27, word2: 32, color: 0x7B8B9E },
    { type: TileType.KIWEN_LA, word1: 27, word2: 33, color: 0x7B8B9E },
    { type: TileType.KIWEN_LEN, word1: 27, word2: 37, color: 0x7B8B9E },
    { type: TileType.KIWEN_LETE, word1: 27, word2: 38, color: 0x7B8B9E },
    { type: TileType.KIWEN_LI, word1: 27, word2: 39, color: 0x7B8B9E },
    { type: TileType.KIWEN_LIPU, word1: 27, word2: 42, color: 0x7B8B9E },
    { type: TileType.KIWEN_LUKA, word1: 27, word2: 45, color: 0x7B8B9E },
    { type: TileType.KIWEN_LUKIN, word1: 27, word2: 46, color: 0x7B8B9E },
    { type: TileType.KIWEN_MAMA, word1: 27, word2: 49, color: 0x7B8B9E },
    { type: TileType.KIWEN_MANI, word1: 27, word2: 50, color: 0x7B8B9E },
    { type: TileType.KIWEN_MI, word1: 27, word2: 52, color: 0x7B8B9E },
    { type: TileType.KIWEN_MOKU, word1: 27, word2: 54, color: 0x7B8B9E },
    { type: TileType.KIWEN_MOLI, word1: 27, word2: 55, color: 0x7B8B9E },
    { type: TileType.KIWEN_NASIN, word1: 27, word2: 63, color: 0x7B8B9E },
    { type: TileType.KIWEN_NI, word1: 27, word2: 65, color: 0x7B8B9E },
    { type: TileType.KIWEN_NIMI, word1: 27, word2: 66, color: 0x7B8B9E },
    { type: TileType.KIWEN_NOKA, word1: 27, word2: 67, color: 0x7B8B9E },
    { type: TileType.KIWEN_O, word1: 27, word2: 68, color: 0x7B8B9E },
    { type: TileType.KIWEN_OLIN, word1: 27, word2: 69, color: 0x7B8B9E },
    { type: TileType.KIWEN_ONA, word1: 27, word2: 70, color: 0x7B8B9E },
    { type: TileType.KIWEN_PAKALA, word1: 27, word2: 72, color: 0x7B8B9E },
    { type: TileType.KIWEN_PAN, word1: 27, word2: 75, color: 0x7B8B9E },
    { type: TileType.KIWEN_PANA, word1: 27, word2: 76, color: 0x7B8B9E },
    { type: TileType.KIWEN_PI, word1: 27, word2: 77, color: 0x7B8B9E },
    { type: TileType.KIWEN_PIMEJA, word1: 27, word2: 79, color: 0x7B8B9E },
    { type: TileType.KIWEN_PIPI, word1: 27, word2: 81, color: 0x7B8B9E },
    { type: TileType.KIWEN_POKI, word1: 27, word2: 83, color: 0x7B8B9E },
    { type: TileType.KIWEN_PU, word1: 27, word2: 85, color: 0x7B8B9E },
    { type: TileType.KIWEN_SELI, word1: 27, word2: 87, color: 0x7B8B9E },
    { type: TileType.KIWEN_SEME, word1: 27, word2: 89, color: 0x7B8B9E },
    { type: TileType.KIWEN_SIJELO, word1: 27, word2: 91, color: 0x7B8B9E },
    { type: TileType.KIWEN_SOWELI, word1: 27, word2: 98, color: 0x7B8B9E },
    { type: TileType.KIWEN_SUNO, word1: 27, word2: 100, color: 0x7B8B9E },
    { type: TileType.KIWEN_SUPA, word1: 27, word2: 101, color: 0x7B8B9E },
    { type: TileType.KIWEN_TOKI, word1: 27, word2: 108, color: 0x7B8B9E },
    { type: TileType.KIWEN_TOMO, word1: 27, word2: 109, color: 0x7B8B9E },
    { type: TileType.KIWEN_UNPA, word1: 27, word2: 111, color: 0x7B8B9E },
    { type: TileType.KIWEN_WAWA, word1: 27, word2: 117, color: 0x7B8B9E },
    { type: TileType.KIWEN_A, word1: 27, word2: 0, color: 0x7B8B9E },
    { type: TileType.KIWEN_AKESI, word1: 27, word2: 1, color: 0x7B8B9E },
    { type: TileType.KIWEN_ALA, word1: 27, word2: 2, color: 0x7B8B9E },
    { type: TileType.KIWEN_ALASA, word1: 27, word2: 3, color: 0x7B8B9E },
    { type: TileType.KIWEN_ALE, word1: 27, word2: 4, color: 0x7B8B9E },
    { type: TileType.KIWEN_ANTE, word1: 27, word2: 6, color: 0x7B8B9E },
    { type: TileType.KIWEN_ANU, word1: 27, word2: 7, color: 0x7B8B9E },
    { type: TileType.KIWEN_AWEN, word1: 27, word2: 8, color: 0x7B8B9E },
    { type: TileType.KIWEN_E, word1: 27, word2: 9, color: 0x7B8B9E },
    { type: TileType.KIWEN_EN, word1: 27, word2: 10, color: 0x7B8B9E },
    { type: TileType.KIWEN_ESUN, word1: 27, word2: 11, color: 0x7B8B9E },
    { type: TileType.KIWEN_IKE, word1: 27, word2: 13, color: 0x7B8B9E },
    { type: TileType.KIWEN_JAKI, word1: 27, word2: 16, color: 0x7B8B9E },
    { type: TileType.KIWEN_KEN, word1: 27, word2: 24, color: 0x7B8B9E },
    { type: TileType.KIWEN_KEPEKEN, word1: 27, word2: 25, color: 0x7B8B9E },
    { type: TileType.KIWEN_KILI, word1: 27, word2: 26, color: 0x7B8B9E },
    { type: TileType.KIWEN_KON, word1: 27, word2: 29, color: 0x7B8B9E },
    { type: TileType.KIWEN_LAPE, word1: 27, word2: 34, color: 0x7B8B9E },
    { type: TileType.KIWEN_LILI, word1: 27, word2: 40, color: 0x7B8B9E },
    { type: TileType.KIWEN_LINJA, word1: 27, word2: 41, color: 0x7B8B9E },
    { type: TileType.KIWEN_LOJE, word1: 27, word2: 43, color: 0x7B8B9E },
    { type: TileType.KIWEN_LON, word1: 27, word2: 44, color: 0x7B8B9E },
    { type: TileType.KIWEN_MELI, word1: 27, word2: 51, color: 0x7B8B9E },
    { type: TileType.KIWEN_MIJE, word1: 27, word2: 53, color: 0x7B8B9E },
    { type: TileType.KIWEN_MONSI, word1: 27, word2: 56, color: 0x7B8B9E },
    { type: TileType.KIWEN_MU, word1: 27, word2: 57, color: 0x7B8B9E },
    { type: TileType.KIWEN_MUN, word1: 27, word2: 58, color: 0x7B8B9E },
    { type: TileType.KIWEN_MUSI, word1: 27, word2: 59, color: 0x7B8B9E },
    { type: TileType.KIWEN_MUTE, word1: 27, word2: 60, color: 0x7B8B9E },
    { type: TileType.KIWEN_NANPA, word1: 27, word2: 61, color: 0x7B8B9E },
    { type: TileType.KIWEN_NASA, word1: 27, word2: 62, color: 0x7B8B9E },
    { type: TileType.KIWEN_NENA, word1: 27, word2: 64, color: 0x7B8B9E },
    { type: TileType.KIWEN_PALI, word1: 27, word2: 73, color: 0x7B8B9E },
    { type: TileType.KIWEN_PALISA, word1: 27, word2: 74, color: 0x7B8B9E },
    { type: TileType.KIWEN_PINI, word1: 27, word2: 80, color: 0x7B8B9E },
    { type: TileType.KIWEN_POKA, word1: 27, word2: 82, color: 0x7B8B9E },
    { type: TileType.KIWEN_PONA, word1: 27, word2: 84, color: 0x7B8B9E },
    { type: TileType.KIWEN_SAMA, word1: 27, word2: 86, color: 0x7B8B9E },
    { type: TileType.KIWEN_SELO, word1: 27, word2: 88, color: 0x7B8B9E },
    { type: TileType.KIWEN_SIKE, word1: 27, word2: 92, color: 0x7B8B9E },
    { type: TileType.KIWEN_SIN, word1: 27, word2: 93, color: 0x7B8B9E },
    { type: TileType.KIWEN_SINA, word1: 27, word2: 94, color: 0x7B8B9E },
    { type: TileType.KIWEN_SINPIN, word1: 27, word2: 95, color: 0x7B8B9E },
    { type: TileType.KIWEN_SITELEN, word1: 27, word2: 96, color: 0x7B8B9E },
    { type: TileType.KIWEN_SONA, word1: 27, word2: 97, color: 0x7B8B9E },
    { type: TileType.KIWEN_SULI, word1: 27, word2: 99, color: 0x7B8B9E },
    { type: TileType.KIWEN_SUWI, word1: 27, word2: 102, color: 0x7B8B9E },
    { type: TileType.KIWEN_TAN, word1: 27, word2: 103, color: 0x7B8B9E },
    { type: TileType.KIWEN_TASO, word1: 27, word2: 104, color: 0x7B8B9E },
    { type: TileType.KIWEN_TAWA, word1: 27, word2: 105, color: 0x7B8B9E },
    { type: TileType.KIWEN_TELO, word1: 27, word2: 106, color: 0x7B8B9E },
    { type: TileType.KIWEN_TENPO, word1: 27, word2: 107, color: 0x7B8B9E },
    { type: TileType.KIWEN_TU, word1: 27, word2: 110, color: 0x7B8B9E },
    { type: TileType.KIWEN_UTA, word1: 27, word2: 112, color: 0x7B8B9E },
    { type: TileType.KIWEN_UTALA, word1: 27, word2: 113, color: 0x7B8B9E },
    { type: TileType.KIWEN_WALO, word1: 27, word2: 114, color: 0x7B8B9E },
    { type: TileType.KIWEN_WAN, word1: 27, word2: 115, color: 0x7B8B9E },
    { type: TileType.KIWEN_WASO, word1: 27, word2: 116, color: 0x7B8B9E },
    { type: TileType.KIWEN_WEKA, word1: 27, word2: 118, color: 0x7B8B9E },
    { type: TileType.KIWEN_WILE, word1: 27, word2: 119, color: 0x7B8B9E },
    // 12 kiwen word slots (ilo, insa, kasi, kiwen, kule, laso, lawa, lupa, ma, open, pilin, sewi)
    // are covered by core tiles (FLINT, AMETHYST, GRASS, STONE, OPAL, TURQUOISE, SKULL, PORTAL, DIRT, RUNE, RED_STONE, SKY)
    // ko (sands) — sandy tan 0xC2B280
    { type: TileType.KO_ESUN, word1: 28, word2: 11, color: 0xC2B280 },
    { type: TileType.KO_IJO, word1: 28, word2: 12, color: 0xC2B280 },
    { type: TileType.KO_IKE, word1: 28, word2: 13, color: 0xC2B280 },
    { type: TileType.KO_ILO, word1: 28, word2: 14, color: 0xC2B280 },
    { type: TileType.KO_JAKI, word1: 28, word2: 16, color: 0xC2B280 },
    { type: TileType.KO_JAN, word1: 28, word2: 17, color: 0xC2B280 },
    { type: TileType.KO_JELO, word1: 28, word2: 18, color: 0xC2B280 },
    { type: TileType.KO_KASI, word1: 28, word2: 23, color: 0xC2B280 },
    { type: TileType.KO_KIWEN, word1: 28, word2: 27, color: 0xC2B280 },
    { type: TileType.KO_KO, word1: 28, word2: 28, color: 0xC2B280 },
    { type: TileType.KO_KULE, word1: 28, word2: 30, color: 0xC2B280 },
    { type: TileType.KO_KULUPU, word1: 28, word2: 31, color: 0xC2B280 },
    { type: TileType.KO_LAPE, word1: 28, word2: 34, color: 0xC2B280 },
    { type: TileType.KO_LILI, word1: 28, word2: 40, color: 0xC2B280 },
    { type: TileType.KO_LOJE, word1: 28, word2: 43, color: 0xC2B280 },
    { type: TileType.KO_MOKU, word1: 28, word2: 54, color: 0xC2B280 },
    { type: TileType.KO_MUN, word1: 28, word2: 58, color: 0xC2B280 },
    { type: TileType.KO_NI, word1: 28, word2: 65, color: 0xC2B280 },
    { type: TileType.KO_O, word1: 28, word2: 68, color: 0xC2B280 },
    { type: TileType.KO_PAN, word1: 28, word2: 75, color: 0xC2B280 },
    { type: TileType.KO_PIMEJA, word1: 28, word2: 79, color: 0xC2B280 },
    { type: TileType.KO_PU, word1: 28, word2: 85, color: 0xC2B280 },
    { type: TileType.KO_SELI, word1: 28, word2: 87, color: 0xC2B280 },
    { type: TileType.KO_SEWI, word1: 28, word2: 90, color: 0xC2B280 },
    { type: TileType.KO_SUWI, word1: 28, word2: 102, color: 0xC2B280 },
    { type: TileType.KO_TELO, word1: 28, word2: 106, color: 0xC2B280 },
    { type: TileType.KO_TENPO, word1: 28, word2: 107, color: 0xC2B280 },
    { type: TileType.KO_UNPA, word1: 28, word2: 111, color: 0xC2B280 },
    { type: TileType.KO_UTALA, word1: 28, word2: 113, color: 0xC2B280 },
    { type: TileType.KO_WALO, word1: 28, word2: 114, color: 0xC2B280 },
    { type: TileType.KO_WASO, word1: 28, word2: 116, color: 0xC2B280 },
];

// Populate TILE_COLORS, TILE_DICTIONARY_MAP, and BLOCK_SPAWN_COMMANDS from placeholder data
for (const pt of PLACEHOLDER_TILES) {
    TILE_COLORS[pt.type] = pt.color;
    TILE_DICTIONARY_MAP[pt.type] = [pt.word1, pt.word2];
    BLOCK_SPAWN_COMMANDS.push({
        phrase: [68, 73, pt.word1, pt.word2], // o pali <word1> <word2>
        blockType: pt.type,
    });
}

// ============================================================
// JUMP HEIGHT COMMAND CONSTANTS
// "o suli sewi <amount>" — increase jump height
// "o lili sewi <amount>" — decrease jump height (min 0)
// ============================================================

export const JUMP_HEIGHT_COMMANDS: { phrase: number[]; delta: number }[] = [
    { phrase: [68, 99, 90, 115], delta: 1 },   // o suli sewi wan   (+1)
    { phrase: [68, 99, 90, 110], delta: 2 },   // o suli sewi tu    (+2)
    { phrase: [68, 99, 90, 45],  delta: 5 },   // o suli sewi luka  (+5)
    { phrase: [68, 99, 90, 60],  delta: 10 },  // o suli sewi mute  (+10)
    { phrase: [68, 99, 90, 4],   delta: 10000 }, // o suli sewi ale (+10,000)
    { phrase: [68, 40, 90, 115], delta: -1 },  // o lili sewi wan   (-1)
    { phrase: [68, 40, 90, 110], delta: -2 },  // o lili sewi tu    (-2)
    { phrase: [68, 40, 90, 45],  delta: -5 },  // o lili sewi luka  (-5)
    { phrase: [68, 40, 90, 60],  delta: -10 }, // o lili sewi mute  (-10)
    { phrase: [68, 40, 90, 4],   delta: -10000 }, // o lili sewi ale (-10,000)
];

// ============================================================
// JUMP HEIGHT OTHER-PLAYER COMMAND CONSTANTS
// "sina o suli sewi <amount>" — increase nearest player's jump height
// "sina o lili sewi <amount>" — decrease nearest player's jump height (min 0)
// ============================================================

export const JUMP_HEIGHT_OTHER_COMMANDS: { phrase: number[]; delta: number }[] = [
    { phrase: [94, 68, 99, 90, 115], delta: 1 },   // sina o suli sewi wan   (+1)
    { phrase: [94, 68, 99, 90, 110], delta: 2 },   // sina o suli sewi tu    (+2)
    { phrase: [94, 68, 99, 90, 45],  delta: 5 },   // sina o suli sewi luka  (+5)
    { phrase: [94, 68, 99, 90, 60],  delta: 10 },  // sina o suli sewi mute  (+10)
    { phrase: [94, 68, 99, 90, 4],   delta: 10000 }, // sina o suli sewi ale (+10,000)
    { phrase: [94, 68, 40, 90, 115], delta: -1 },  // sina o lili sewi wan   (-1)
    { phrase: [94, 68, 40, 90, 110], delta: -2 },  // sina o lili sewi tu    (-2)
    { phrase: [94, 68, 40, 90, 45],  delta: -5 },  // sina o lili sewi luka  (-5)
    { phrase: [94, 68, 40, 90, 60],  delta: -10 }, // sina o lili sewi mute  (-10)
    { phrase: [94, 68, 40, 90, 4],   delta: -10000 }, // sina o lili sewi ale (-10,000)
];

// ============================================================
// BREATHING / DROWNING CONSTANTS
// ============================================================

export const BUBBLE_COUNT = 10;                // number of bubble icons in the HUD
export const TICKS_PER_BUBBLE = 4;             // ticks per bubble (4 stages: full, shrink1, shrink2, pop)
export const MAX_AIR_TICKS = BUBBLE_COUNT * TICKS_PER_BUBBLE; // 40 total underwater ticks
export const DROWNING_DAMAGE_INTERVAL = 4;     // ticks between drowning damage once air is depleted
export const DROWNING_DAMAGE = 1;              // hearts of damage per drowning tick

// ============================================================
// NPC MENTOR CONSTANTS
// The mentor ("jan pu" / Sage) stands on the spawn platform.
// Position is relative to the portal surface Y calculated from the seed.
// ============================================================

export const MENTOR_PLATFORM_X = SPAWN_CENTER_X; // world X on the spawn platform
export const MENTOR_FACING = 1;      // faces right (toward portal / arriving players)

// ============================================================
// EXPLORATION HOUSE CONSTANTS
// A red stone (kiwen pilin) building 16 tiles right of spawn.
// Both houses sit left of Yggdrasil.
// Only generated at world creation, not restored on player join.
// ============================================================

export const EXPLORATION_HOUSE_CENTER_X = SPAWN_CENTER_X + 16; // 16 tiles right of spawn house

// Lover NPC spawns at the center of the exploration house platform
export const LOVER_HOME_X = EXPLORATION_HOUSE_CENTER_X;

// ============================================================
// UNDERGROUND ROOM CONSTANTS
// A large empty room below the spawn house, accessible via ladder.
// Occupies the cosmology pixel BELOW the spawn clearing (pixel 31, 58).
// Uses absolute Y coordinates (not relative to portalSurfaceY).
// ============================================================

export const UNDERGROUND_ROOM_X1 = SPAWN_CENTER_X - 44;  // left edge (inclusive)
export const UNDERGROUND_ROOM_X2 = SPAWN_CENTER_X + 5;   // right edge (inclusive)
export const UNDERGROUND_ROOM_Y1 = UNDERGROUND_LEVEL + 4;  // top edge (inclusive), just below deepest possible dirt layer
export const UNDERGROUND_ROOM_Y2 = UNDERGROUND_ROOM_Y1 + 25;  // bottom edge (inclusive), 26-tile height
export const UNDERGROUND_LADDER_BOTTOM = UNDERGROUND_ROOM_Y1 + 12;  // Y where ladder ends inside the room (same relative offset)
export const UNDERGROUND_SPAWN_X = UNDERGROUND_ROOM_X1 + 4; // cavern local x=5
export const UNDERGROUND_SPAWN_Y = UNDERGROUND_ROOM_Y1 + 4; // cavern local y=5

// Hero NPC (jan wawa) — lives in underground cavern rect7
export const HERO_SPAWN_REL_X = 20;   // cavern-local 1-based X
export const HERO_SPAWN_REL_Y = 17;   // cavern-local 1-based Y
export const HERO_MIN_REL_X = 19;     // left walkable bound (wall at 18)
export const HERO_MAX_REL_X = 23;     // right walkable bound (wall at 24)

// ============================================================
// EULER SQUARE SPAWN FORMULAS
// Deterministic placement based on world coordinates using
// linear combinations mod prime. Coefficient must be coprime
// to modulus for even diagonal distribution (Latin square property).
// ============================================================

/** 1-in-10 density. Leaves → apple vs stick. */
export function isApple(x: number, y: number): boolean {
    return ((x + 3 * y) % 10) === 0;
}

/** 1-in-19 density. Grass surface → rats nest at terrain edges. */
export function isNestSite(x: number, y: number): boolean {
    return ((x + 11 * y) % 19) === 0;
}

/** 1-in-97 density. Stone underground → ore vein seed point. */
export function isOreSeed(x: number, y: number): boolean {
    return ((x + 13 * y) % 97) === 0;
}

// Reverse lookup: biome word index → KIWEN_<word> TileType
const _kiwenWordToTile = new Map<number, TileType>();
for (const pt of PLACEHOLDER_TILES) {
    if (pt.word1 === 27) { // kiwen category
        _kiwenWordToTile.set(pt.word2, pt.type);
    }
}
// Map core tiles to their kiwen word slots (no placeholder duplicates needed)
_kiwenWordToTile.set(14, TileType.FLINT);         // kiwen ilo → flint
_kiwenWordToTile.set(15, TileType.AMETHYST);      // kiwen insa → amethyst
_kiwenWordToTile.set(23, TileType.GRASS);          // kiwen kasi → grass
_kiwenWordToTile.set(27, TileType.STONE);          // kiwen kiwen → stone
_kiwenWordToTile.set(30, TileType.OPAL);           // kiwen kule → opal
_kiwenWordToTile.set(35, TileType.TURQUOISE);     // kiwen laso → turquoise
_kiwenWordToTile.set(36, TileType.SKULL);          // kiwen lawa → skull
_kiwenWordToTile.set(47, TileType.PORTAL);         // kiwen lupa → portal
_kiwenWordToTile.set(48, TileType.DIRT);           // kiwen ma → dirt
_kiwenWordToTile.set(71, TileType.RUNE);           // kiwen open → rune
_kiwenWordToTile.set(78, TileType.RED_STONE);     // kiwen pilin → red stone
_kiwenWordToTile.set(90, TileType.SKY);            // kiwen sewi → sky

/** Get the KIWEN tile type for a biome's word index, or null if none exists. */
export function getKiwenTileForBiome(biomeWord: number): TileType | null {
    return _kiwenWordToTile.get(biomeWord) ?? null;
}

// Reverse lookup: biome word index → KASI_<word> TileType
const _kasiWordToTile = new Map<number, TileType>();
for (const pt of PLACEHOLDER_TILES) {
    if (pt.word1 === 23) { // kasi category
        _kasiWordToTile.set(pt.word2, pt.type);
    }
}
// Map core tiles to their kasi word slots
_kasiWordToTile.set(15, TileType.LOG);             // kasi insa → log
_kasiWordToTile.set(40, TileType.SAPLING);         // kasi lili → sapling
_kasiWordToTile.set(42, TileType.LEAVES);          // kasi lipu → leaves
_kasiWordToTile.set(74, TileType.TRUNK);           // kasi palisa → trunk
_kasiWordToTile.set(101, TileType.PLANK);          // kasi supa → plank

/** Get the KASI tile type for a biome's word index, or null if none exists. */
export function getKasiTileForBiome(biomeWord: number): TileType | null {
    return _kasiWordToTile.get(biomeWord) ?? null;
}

/** 1-in-41 density. Surface shrub seed point for biome-specific kasi clusters. */
export function isShrubSeed(x: number, y: number): boolean {
    return ((x * 7 + y * 3) % 41) === 0;
}

// ============================================================
// EQUIPMENT ITEMS
// Items that go into the player's equipment slot (A-button action)
// rather than being held above the head like normal blocks.
// ============================================================

export function isEquipmentItem(tile: TileType): boolean {
    return tile === TileType.IRON_SWORD || tile === TileType.WOODEN_SHIELD || tile === TileType.STONE_CLUB
        || tile === TileType.ILO_IJO    // club (melee-only sword)
        || tile === TileType.ILO_ANPA   // shovel (dig below)
        || tile === TileType.ILO_LUPA   // drill (disintegrate front)
        || tile === TileType.ILO_POKI   // bucket (water pickup/place)
        || tile === TileType.ILO_JAKI   // plunger (pull from 2 away)
        || tile === TileType.ILO_KILI   // harvest basket
        || tile === TileType.ILO_LON    // compass (HUD line to origin)
        || tile === TileType.ILO_WASO;  // glider (horizontal flight)
}

/** Check if a tool equipment item uses A-button for its tool action (not sword swing). */
export function isToolItem(tile: TileType): boolean {
    return tile === TileType.ILO_IJO    // club
        || tile === TileType.ILO_ANPA   // shovel
        || tile === TileType.ILO_LUPA   // drill
        || tile === TileType.ILO_POKI   // bucket
        || tile === TileType.ILO_JAKI   // plunger
        || tile === TileType.ILO_KILI   // harvest basket
        || tile === TileType.ILO_WASO;  // glider
}

/** Check if a tile is a diggable type for the shovel. */
export function isDiggable(tile: TileType): boolean {
    return tile === TileType.DIRT || tile === TileType.GRASS;
}

/** Check if a tile is one of the expanded KILI fruit tiles (IDs 324-407). */
export function isExpandedKiliFruit(tile: TileType): boolean {
    return tile >= TileType.KILI_A && tile <= TileType.KILI_WEKA;
}

/** Check if a tile is a fruit type for the harvest basket. */
export function isFruitTile(tile: TileType): boolean {
    return tile === TileType.HEART_CONTAINER
        || tile === TileType.KILI_AKESI || tile === TileType.KILI_ALE
        || tile === TileType.KILI_ANPA || tile === TileType.KILI_ESUN
        || tile === TileType.KILI_IJO || tile === TileType.KILI_IKE
        || tile === TileType.KILI_INSA || tile === TileType.KILI_JAKI
        || tile === TileType.KILI_JELO || tile === TileType.KILI_KEN
        || tile === TileType.KILI_KILI || tile === TileType.KILI_KIWEN
        || tile === TileType.KILI_KULUPU || tile === TileType.KILI_LASO
        || tile === TileType.KILI_LAWA || tile === TileType.KILI_LETE
        || tile === TileType.KILI_LINJA || tile === TileType.KILI_LOJE
        || tile === TileType.KILI_MA || tile === TileType.KILI_MOLI
        || tile === TileType.KILI_MUSI || tile === TileType.KILI_MUTE
        || tile === TileType.KILI_NANPA || tile === TileType.KILI_OPEN
        || tile === TileType.KILI_PAKALA || tile === TileType.KILI_PALISA
        || tile === TileType.KILI_PAN || tile === TileType.KILI_PU
        || tile === TileType.KILI_SELI || tile === TileType.KILI_SELO
        || tile === TileType.KILI_SONA || tile === TileType.KILI_SOWELI
        || tile === TileType.KILI_SUWI || tile === TileType.KILI_TELO
        || tile === TileType.KILI_UNPA || tile === TileType.KILI_WILE
        || isExpandedKiliFruit(tile);
}

// ============================================================
// KIWEN BLOCK CONSTANTS
// ============================================================

export const THWOMP_TRIGGER_RANGE = 20;           // tiles below a thwomp that trigger its fall
export const EYE_SWITCH_DURATION_TICKS = 100;      // 10 seconds at 10 ticks/sec
export const COMMAND_BLOCK_COOLDOWN_TICKS = 30;    // 3 seconds between command block replays
export const SIGN_GLYPH_COUNT = 120;               // 120 toki pona glyphs for sign blocks

// ============================================================
// KASI PLANT CONSTANTS
// ============================================================

export const THORNBUSH_DAMAGE = 1;                  // damage per tick from standing in thornbush
export const THORNBUSH_DAMAGE_INTERVAL = 4;         // ticks between thornbush damage hits

// ============================================================
// KILI (FRUIT) CONSTANTS
// ============================================================

/** Healing amount per fruit type. 0 = no healing, -1 = damage. */
export function getFruitHealAmount(tile: TileType): number {
    switch (tile) {
        case TileType.KILI_AKESI: return 5;    // Dragonfruit: heals 5
        case TileType.KILI_JELO: return 1;     // Banana: heals 1
        case TileType.KILI_KEN: return 999;    // Senzu bean: heals to max (clamped at maxHp)
        case TileType.KILI_KULUPU: return -2;  // Grape cluster: slow heal 5 (special, handled separately)
        case TileType.KILI_IKE: return -1;     // Nightshade: damages 1
        default:
            // All expanded kili fruits heal 1 by default
            if (isExpandedKiliFruit(tile)) return 1;
            return 0;
    }
}

/** Check if a fruit is a healing type (consumed when HP < max, held when full). */
export function isHealingFruit(tile: TileType): boolean {
    return tile === TileType.KILI_AKESI
        || tile === TileType.KILI_JELO
        || tile === TileType.KILI_KEN
        || tile === TileType.KILI_KULUPU
        || isExpandedKiliFruit(tile);
}

/** Check if a fruit is destroyable by sword/slash attacks. */
export function isFruitDestroyable(tile: TileType): boolean {
    return isFruitTile(tile) && tile !== TileType.KILI_KIWEN;
}

/** Fruits that can spawn from a cornucopia (KILI_ALE) split. */
export const CORNUCOPIA_FRUITS: TileType[] = [
    TileType.HEART_CONTAINER,  // Apple
    TileType.KILI_AKESI,       // Dragonfruit
    TileType.KILI_JELO,        // Banana
    TileType.KILI_IKE,         // Nightshade
    TileType.KILI_SOWELI,      // Acorn
    TileType.KILI_LAWA,        // Spiky fruit
    TileType.KILI_KIWEN,       // Coconut
    TileType.KILI_KEN,         // Senzu bean
    TileType.KILI_KULUPU,      // Grape cluster
];

export const KILI_KULUPU_HEAL_TOTAL = 5;              // total hearts healed by grape cluster
export const KILI_KULUPU_HEAL_INTERVAL = 10;           // ticks between each 1hp heal (1 second)
export const KILI_NIGHTSHADE_DAMAGE = 1;               // damage from eating nightshade
export const KILI_LAWA_DAMAGE = 1;                     // damage from stepping on spiky fruit
export const KILI_LAWA_DAMAGE_INTERVAL = 4;            // ticks between spiky fruit damage
export const KILI_SOWELI_DAMAGE = 1;                    // damage from acorn overlap

/** Passable hazard fruits — damage on overlap (like thornbush). */
export function isFruitHazard(tile: TileType): boolean {
    return tile === TileType.KILI_LAWA || tile === TileType.KILI_SOWELI;
}

/** Indestructible tile — cannot be picked up, destroyed by sword, or removed in any way. */
export function isIndestructible(tile: TileType): boolean {
    return tile === TileType.KASI_ALE;
}

/** Shrub tile — passable, disappears on any disturbance (sword, pickup, ground removed). */
export function isShrubTile(tile: TileType): boolean {
    return tile === TileType.KASI_IJO;
}

/** Vine tile — functions as a ladder clone. */
export function isVineTile(tile: TileType): boolean {
    return tile === TileType.KASI_LINJA;
}

/** KASI placeholder tile — any KASI block without special tuned behavior. These are passable. */
export function isKasiPlaceholder(tile: TileType): boolean {
    const isKasiRange = (tile >= TileType.KASI_AKESI && tile <= TileType.KASI_WAWA)
        || (tile >= TileType.KASI_A && tile <= TileType.KASI_WILE);
    if (!isKasiRange) return false;
    // Exclude tiles with special tuned behavior
    return tile !== TileType.KASI_ALE      // indestructible (stays solid)
        && tile !== TileType.KASI_IJO      // shrub (already passable, has support check)
        && tile !== TileType.KASI_UTALA    // thornbush (already passable, has damage)
        && tile !== TileType.KASI_LINJA;   // vine (ladder/climbable)
}

/** Food tiles that can spawn from a ? block head-bonk. */
export const QUESTION_BLOCK_FOODS: TileType[] = [
    TileType.HEART_CONTAINER,
    TileType.KILI_JELO,
    TileType.KILI_LOJE,
    TileType.KILI_SUWI,
    TileType.KILI_SELI,
    TileType.KILI_LASO,
];

// ============================================================
// DICE (ILO_ANU) CONSTANTS
// ============================================================

export const DICE_FACES = 6;

/** Deterministic dice roll based on world seed and placement count. */
export function rollDice(seed: number, rollCount: number): number {
    // Simple hash: seed XOR rollCount, multiply by prime, mod 6
    let h = (seed ^ rollCount) >>> 0;
    h = Math.imul(h, 2654435761) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return (h % DICE_FACES) + 1; // 1-6
}

// ============================================================
// HARVEST BASKET CONSTANTS
// ============================================================

export const BASKET_CAPACITY = 7;

// ============================================================
// TOOL ACTION MESSAGE TYPES
// ============================================================

/** Check if a tile is a one-way platform (passable from below, solid from above). */
export function isOneWayPlatform(tile: TileType): boolean {
    return tile === TileType.KIWEN_SUPA;
}

/** Check if a tile behaves like SKULL for crafting (SKULL + STICK → SAPLING → Tree). */
export function isBoneLikeBlock(tile: TileType): boolean {
    return tile === TileType.SKULL || tile === TileType.KIWEN_SOWELI;
}

export type ToolActionType =
    | 'shovel_dig'      // ILO_ANPA: dig block below onto head
    | 'drill_front'     // ILO_LUPA: disintegrate block in front
    | 'drill_below'     // ILO_LUPA: disintegrate block below (A+down)
    | 'plunger_pull'    // ILO_JAKI: pull block from 2 tiles away
    | 'bucket_fill'     // ILO_POKI: pick up water
    | 'bucket_pour'     // ILO_POKI: place water
    | 'basket_collect'  // ILO_KILI: collect fruit on collision
    | 'basket_place'    // ILO_KILI: place last fruit out
    | 'dice_place';     // ILO_ANU: place dice with roll value

// ============================================================
// KO (SAND) CONSTANTS
// ============================================================

/** Check if a tile is a sand type (ko category). All sands are gravity blocks. */
export function isSandTile(tile: TileType): boolean {
    return tile >= TileType.KO_ESUN && tile <= TileType.KO_WASO;
}

/** Check if a tile is quicksand (KO_IKE). Quicksand prevents step-up. */
export function isQuicksand(tile: TileType): boolean {
    return tile === TileType.KO_IKE;
}

// ============================================================
// SUFFOCATION CONSTANTS
// ============================================================

export const SUFFOCATION_DAMAGE = 1;
export const SUFFOCATION_DAMAGE_INTERVAL = 4;  // ticks between suffocation damage

/** Check if a tile suffocates players when their head is inside it.
 *  Solid blocks that are not food, ladder, passable, or body-passable cause suffocation. */

// ============================================================
// BIOME PALETTE SYSTEM
// Each of the 120 toki pona words defines a biome triad:
//   biome = ma X, stone = kiwen X, plant = kasi X
// Grouped by cosmological zone for display ordering.
// ============================================================

export enum BiomeZone {
    VOID_OUROBOROS = 0,
    SKY_FIRMAMENT = 1,
    SACRED_MOUNTAIN = 2,
    SURFACE_NORTH = 3,
    SURFACE_SOUTH = 4,
    SURFACE_WEST = 5,
    SURFACE_EAST = 6,
    SURFACE_CENTER = 7,
    SURFACE_UNALIGNED = 8,
    SURROUNDING_SEA = 9,
    FOUR_RIVERS = 10,
    UNDERWORLD = 11,
    TARTARUS = 12,
    LIMINAL = 13,
}

/** Display name for each zone (shown as section headers in palette UI). */
export const BIOME_ZONE_NAMES: Record<BiomeZone, string> = {
    [BiomeZone.VOID_OUROBOROS]: 'Void',
    [BiomeZone.SKY_FIRMAMENT]: 'Sky',
    [BiomeZone.SACRED_MOUNTAIN]: 'Mountain',
    [BiomeZone.SURFACE_NORTH]: 'North',
    [BiomeZone.SURFACE_SOUTH]: 'South',
    [BiomeZone.SURFACE_WEST]: 'West',
    [BiomeZone.SURFACE_EAST]: 'East',
    [BiomeZone.SURFACE_CENTER]: 'Center',
    [BiomeZone.SURFACE_UNALIGNED]: 'Unaligned',
    [BiomeZone.SURROUNDING_SEA]: 'Sea',
    [BiomeZone.FOUR_RIVERS]: 'Rivers',
    [BiomeZone.UNDERWORLD]: 'Underworld',
    [BiomeZone.TARTARUS]: 'Tartarus',
    [BiomeZone.LIMINAL]: 'Liminal',
};

/** Cosmostructure glyph pair for each zone (suli X). Used as zone header icons. */
export const BIOME_ZONE_GLYPHS: Record<BiomeZone, [number, number]> = {
    [BiomeZone.VOID_OUROBOROS]: [99, 2],      // suli ala
    [BiomeZone.SKY_FIRMAMENT]: [99, 29],      // suli kon
    [BiomeZone.SACRED_MOUNTAIN]: [99, 64],    // suli nena
    [BiomeZone.SURFACE_NORTH]: [99, 97],      // suli sona
    [BiomeZone.SURFACE_SOUTH]: [99, 87],      // suli seli
    [BiomeZone.SURFACE_WEST]: [99, 27],       // suli kiwen
    [BiomeZone.SURFACE_EAST]: [99, 6],        // suli ante
    [BiomeZone.SURFACE_CENTER]: [99, 48],     // suli ma
    [BiomeZone.SURFACE_UNALIGNED]: [48, 48],  // ma ma (no cosmostructure)
    [BiomeZone.SURROUNDING_SEA]: [99, 106],   // suli telo
    [BiomeZone.FOUR_RIVERS]: [99, 63],        // suli nasin
    [BiomeZone.UNDERWORLD]: [99, 47],         // suli lupa
    [BiomeZone.TARTARUS]: [99, 5],            // suli anpa
    [BiomeZone.LIMINAL]: [99, 99],            // suli suli
};

export interface BiomePaletteEntry {
    /** Word index (0-119) for the modifier — the X in ma X / kiwen X / kasi X */
    word: number;
    /** Cosmological zone this biome belongs to */
    zone: BiomeZone;
}

/** Well-known word indices used by the palette system. */
export const PALETTE_WORD_MA = 48;     // "ma" — biome prefix
export const PALETTE_WORD_KIWEN = 27;  // "kiwen" — stone prefix
export const PALETTE_WORD_KASI = 23;   // "kasi" — plant prefix

/**
 * All 120 biome palette entries, ordered by cosmological zone then by
 * inner-to-outer / mild-to-extreme position within each zone.
 * Derived from docs/biome-palette-lists.md.
 */
export const BIOME_PALETTES: BiomePaletteEntry[] = [
    // ── Void / Ouroboros Edge (3) ──
    { word: 2, zone: BiomeZone.VOID_OUROBOROS },     // ma ala — The Void
    { word: 92, zone: BiomeZone.VOID_OUROBOROS },    // ma sike — Ring Formation
    { word: 94, zone: BiomeZone.VOID_OUROBOROS },    // ma sina — Counter-World

    // ── Sky / Firmament (8) ──
    { word: 29, zone: BiomeZone.SKY_FIRMAMENT },     // ma kon — Cloud Sea
    { word: 90, zone: BiomeZone.SKY_FIRMAMENT },     // ma sewi — Floating Isle
    { word: 116, zone: BiomeZone.SKY_FIRMAMENT },    // ma waso — Nesting Cliff
    { word: 58, zone: BiomeZone.SKY_FIRMAMENT },     // ma mun — Moonscape
    { word: 100, zone: BiomeZone.SKY_FIRMAMENT },    // ma suno — Solar Terrace
    { word: 52, zone: BiomeZone.SKY_FIRMAMENT },     // ma mi — Starfield
    { word: 4, zone: BiomeZone.SKY_FIRMAMENT },      // ma ale — Celestial Sphere
    { word: 89, zone: BiomeZone.SKY_FIRMAMENT },     // ma seme — Aurora Veil

    // ── Sacred Mountain (6) ──
    { word: 64, zone: BiomeZone.SACRED_MOUNTAIN },   // ma nena — Sacred Peak
    { word: 0, zone: BiomeZone.SACRED_MOUNTAIN },    // ma a — Root Altar
    { word: 44, zone: BiomeZone.SACRED_MOUNTAIN },   // ma lon — Mountain Pass
    { word: 85, zone: BiomeZone.SACRED_MOUNTAIN },   // ma pu — Shrine Terrace
    { word: 77, zone: BiomeZone.SACRED_MOUNTAIN },   // ma pi — Alpine Meadow
    { word: 115, zone: BiomeZone.SACRED_MOUNTAIN },  // ma wan — Lonely Peak

    // ── Surface — North / Tortoise (9) ──
    { word: 38, zone: BiomeZone.SURFACE_NORTH },     // ma lete — Tundra
    { word: 97, zone: BiomeZone.SURFACE_NORTH },     // ma sona — Standing Stones
    { word: 86, zone: BiomeZone.SURFACE_NORTH },     // ma sama — Mirror Lake
    { word: 34, zone: BiomeZone.SURFACE_NORTH },     // ma lape — Ruin Meadow
    { word: 8, zone: BiomeZone.SURFACE_NORTH },      // ma awen — Petrified Forest
    { word: 106, zone: BiomeZone.SURFACE_NORTH },    // ma telo — Wetland
    { word: 32, zone: BiomeZone.SURFACE_NORTH },     // ma kute — Wind Hollow
    { word: 114, zone: BiomeZone.SURFACE_NORTH },    // ma walo — Salt Flat
    { word: 118, zone: BiomeZone.SURFACE_NORTH },    // ma weka — Eroded Waste

    // ── Surface — South / Phoenix (9) ──
    { word: 87, zone: BiomeZone.SURFACE_SOUTH },     // ma seli — Magma Field
    { word: 43, zone: BiomeZone.SURFACE_SOUTH },     // ma loje — Red Dust Basin
    { word: 18, zone: BiomeZone.SURFACE_SOUTH },     // ma jelo — Golden Savanna
    { word: 26, zone: BiomeZone.SURFACE_SOUTH },     // ma kili — Fruiting Grove
    { word: 75, zone: BiomeZone.SURFACE_SOUTH },     // ma pan — Grain Field
    { word: 76, zone: BiomeZone.SURFACE_SOUTH },     // ma pana — Geyser Flat
    { word: 93, zone: BiomeZone.SURFACE_SOUTH },     // ma sin — Volcanic Newland
    { word: 102, zone: BiomeZone.SURFACE_SOUTH },    // ma suwi — Honey Glade
    { word: 111, zone: BiomeZone.SURFACE_SOUTH },    // ma unpa — Lush Thicket

    // ── Surface — West / Lion (9) ──
    { word: 27, zone: BiomeZone.SURFACE_WEST },      // ma kiwen — Stone Barrens
    { word: 14, zone: BiomeZone.SURFACE_WEST },      // ma ilo — Quarry
    { word: 113, zone: BiomeZone.SURFACE_WEST },     // ma utala — Battlefield
    { word: 95, zone: BiomeZone.SURFACE_WEST },      // ma sinpin — Cliff Face
    { word: 73, zone: BiomeZone.SURFACE_WEST },      // ma pali — Slag Heap
    { word: 42, zone: BiomeZone.SURFACE_WEST },      // ma lipu — Mesa
    { word: 28, zone: BiomeZone.SURFACE_WEST },      // ma ko — Sand Flat
    { word: 88, zone: BiomeZone.SURFACE_WEST },      // ma selo — Canyon Rim
    { word: 101, zone: BiomeZone.SURFACE_WEST },     // ma supa — Plateau

    // ── Surface — East / Dragon (10) ──
    { word: 6, zone: BiomeZone.SURFACE_EAST },       // ma ante — Tremor Flats
    { word: 22, zone: BiomeZone.SURFACE_EAST },      // ma kama — Seedling Waste
    { word: 62, zone: BiomeZone.SURFACE_EAST },      // ma nasa — Fungal Bloom
    { word: 74, zone: BiomeZone.SURFACE_EAST },      // ma palisa — Bamboo Thicket
    { word: 78, zone: BiomeZone.SURFACE_EAST },      // ma pilin — Glowmoss Glade
    { word: 1, zone: BiomeZone.SURFACE_EAST },       // ma akesi — Serpent Marsh
    { word: 41, zone: BiomeZone.SURFACE_EAST },      // ma linja — Vine Choke
    { word: 24, zone: BiomeZone.SURFACE_EAST },      // ma ken — Mist Thicket
    { word: 30, zone: BiomeZone.SURFACE_EAST },      // ma kule — Prismatic Springs
    { word: 91, zone: BiomeZone.SURFACE_EAST },      // ma sijelo — Flesh Mire

    // ── Surface — Center / Neutral (13) ──
    { word: 23, zone: BiomeZone.SURFACE_CENTER },    // ma kasi — Heartwood Grove
    { word: 15, zone: BiomeZone.SURFACE_CENTER },    // ma insa — Tree Hollow
    { word: 48, zone: BiomeZone.SURFACE_CENTER },    // ma ma — Heartland
    { word: 12, zone: BiomeZone.SURFACE_CENTER },    // ma ijo — Open Plains
    { word: 17, zone: BiomeZone.SURFACE_CENTER },    // ma jan — Settlement
    { word: 71, zone: BiomeZone.SURFACE_CENTER },    // ma open — Spawn Clearing
    { word: 84, zone: BiomeZone.SURFACE_CENTER },    // ma pona — Blessed Ground
    { word: 63, zone: BiomeZone.SURFACE_CENTER },    // ma nasin — Pilgrims' Road
    { word: 7, zone: BiomeZone.SURFACE_CENTER },     // ma anu — Biome Border
    { word: 9, zone: BiomeZone.SURFACE_CENTER },     // ma e — Tilled Soil
    { word: 11, zone: BiomeZone.SURFACE_CENTER },    // ma esun — Trade Crossroads
    { word: 36, zone: BiomeZone.SURFACE_CENTER },    // ma lawa — Throne Ruin
    { word: 108, zone: BiomeZone.SURFACE_CENTER },   // ma toki — Babel Ruin

    // ── Surface — Unaligned (10) ──
    { word: 3, zone: BiomeZone.SURFACE_UNALIGNED },  // ma alasa — Hunting Steppe
    { word: 19, zone: BiomeZone.SURFACE_UNALIGNED }, // ma jo — Vein Valley
    { word: 51, zone: BiomeZone.SURFACE_UNALIGNED }, // ma meli — Fertile Crescent
    { word: 57, zone: BiomeZone.SURFACE_UNALIGNED }, // ma mu — Migration Trail
    { word: 67, zone: BiomeZone.SURFACE_UNALIGNED }, // ma noka — Valley Floor
    { word: 69, zone: BiomeZone.SURFACE_UNALIGNED }, // ma olin — Paradise Garden
    { word: 99, zone: BiomeZone.SURFACE_UNALIGNED }, // ma suli — Titan's Rest
    { word: 49, zone: BiomeZone.SURFACE_UNALIGNED }, // ma mama — Nesting Basin
    { word: 83, zone: BiomeZone.SURFACE_UNALIGNED }, // ma poki — Basin
    { word: 105, zone: BiomeZone.SURFACE_UNALIGNED },// ma tawa — Nomad's Corridor

    // ── Surrounding Sea (8) ──
    { word: 20, zone: BiomeZone.SURROUNDING_SEA },   // ma kala — Coral Shallows
    { word: 35, zone: BiomeZone.SURROUNDING_SEA },   // ma laso — Deep Water
    { word: 60, zone: BiomeZone.SURROUNDING_SEA },   // ma mute — Archipelago
    { word: 104, zone: BiomeZone.SURROUNDING_SEA },  // ma taso — Lone Island
    { word: 70, zone: BiomeZone.SURROUNDING_SEA },   // ma ona — Foreign Shore
    { word: 112, zone: BiomeZone.SURROUNDING_SEA },  // ma uta — River Mouth
    { word: 50, zone: BiomeZone.SURROUNDING_SEA },   // ma mani — Shallows Pasture
    { word: 119, zone: BiomeZone.SURROUNDING_SEA },  // ma wile — Siren Shore

    // ── Four Rivers (4) ──
    { word: 66, zone: BiomeZone.FOUR_RIVERS },       // ma nimi — White River Banks
    { word: 82, zone: BiomeZone.FOUR_RIVERS },       // ma poka — Black River Shore
    { word: 10, zone: BiomeZone.FOUR_RIVERS },       // ma en — Red River Ford
    { word: 98, zone: BiomeZone.FOUR_RIVERS },       // ma soweli — Magenta River Crossing

    // ── Underworld (23) ──
    { word: 47, zone: BiomeZone.UNDERWORLD },        // ma lupa — Cave Mouth (L1)
    { word: 37, zone: BiomeZone.UNDERWORLD },        // ma len — Frost Cavern (L1)
    { word: 33, zone: BiomeZone.UNDERWORLD },        // ma la — Gate Passage (L1)
    { word: 46, zone: BiomeZone.UNDERWORLD },        // ma lukin — Overlook (L2)
    { word: 45, zone: BiomeZone.UNDERWORLD },        // ma luka — Chasm Ledge (L2)
    { word: 21, zone: BiomeZone.UNDERWORLD },        // ma kalama — Echo Cavern (L3)
    { word: 79, zone: BiomeZone.UNDERWORLD },        // ma pimeja — Shadow Reach (L3)
    { word: 65, zone: BiomeZone.UNDERWORLD },        // ma ni — Hall of the Present (L3)
    { word: 54, zone: BiomeZone.UNDERWORLD },        // ma moku — Barren Table (L3)
    { word: 39, zone: BiomeZone.UNDERWORLD },        // ma li — Fault Line (L4)
    { word: 81, zone: BiomeZone.UNDERWORLD },        // ma pipi — Hive Mound (L4)
    { word: 25, zone: BiomeZone.UNDERWORLD },        // ma kepeken — Ore Vein (L4)
    { word: 53, zone: BiomeZone.UNDERWORLD },        // ma mije — Warrior Crypt (L4)
    { word: 13, zone: BiomeZone.UNDERWORLD },        // ma ike — Blighted Waste (L5)
    { word: 16, zone: BiomeZone.UNDERWORLD },        // ma jaki — Tar Bog (L5)
    { word: 55, zone: BiomeZone.UNDERWORLD },        // ma moli — Boneyard (L5)
    { word: 68, zone: BiomeZone.UNDERWORLD },        // ma o — Ordeal Pit (L5)
    { word: 72, zone: BiomeZone.UNDERWORLD },        // ma pakala — Crater Field (L5)
    { word: 96, zone: BiomeZone.UNDERWORLD },        // ma sitelen — Geoglyph Plain (L6)
    { word: 109, zone: BiomeZone.UNDERWORLD },       // ma tomo — Ruin City (L6)
    { word: 31, zone: BiomeZone.UNDERWORLD },        // ma kulupu — Prison Colony (L6)
    { word: 80, zone: BiomeZone.UNDERWORLD },        // ma pini — Fossil Shelf (L7)
    { word: 103, zone: BiomeZone.UNDERWORLD },       // ma tan — Origin Depth (L7)

    // ── Tartarus (2) ──
    { word: 5, zone: BiomeZone.TARTARUS },           // ma anpa — Abyss Floor
    { word: 117, zone: BiomeZone.TARTARUS },         // ma wawa — Maelstrom

    // ── Liminal / Meta (6) ──
    { word: 110, zone: BiomeZone.LIMINAL },          // ma tu — Continental Divide
    { word: 40, zone: BiomeZone.LIMINAL },           // ma lili — Moss Garden
    { word: 61, zone: BiomeZone.LIMINAL },           // ma nanpa — Crystal Grid
    { word: 59, zone: BiomeZone.LIMINAL },           // ma musi — Arena
    { word: 56, zone: BiomeZone.LIMINAL },           // ma monsi — Hinterland
    { word: 107, zone: BiomeZone.LIMINAL },          // ma tenpo — Strata Shelf
];
export function isSuffocating(tile: TileType): boolean {
    // Not suffocating if the tile is passable, ladder, or body-passable (those include food, etc.)
    // We only need to check isPassable here since isBodyPassable = isPassable || isLadder || one-way
    // But we import those from physics, not from here. So we do a simple check:
    // Suffocating = any tile that is NOT SKY, PORTAL, TRUNK, SAPLING, WATER_SURFACE, BOOTS,
    //   YELLOW_DOOR_OPEN, YELLOW_KEY, LADDER, LEAVES, STICK, WATER, KASI_LINJA, KIWEN_SUPA,
    //   KASI_IJO, KASI_UTALA, KILI_LAWA, KILI_SOWELI, or any other passable/body-passable tile.
    // Simplification: tile is suffocating if it's not SKY (0) and not a known non-solid.
    // Actually, the cleanest approach: suffocating = solid block = not passable and not ladder and not one-way platform
    // We'll just inline the check here to avoid circular imports.
    const passable = tile === TileType.SKY || tile === TileType.PORTAL || tile === TileType.TRUNK
        || tile === TileType.SAPLING || tile === TileType.WATER_SURFACE || tile === TileType.BOOTS
        || tile === TileType.YELLOW_DOOR_OPEN || tile === TileType.YELLOW_KEY
        || tile === TileType.KASI_IJO || tile === TileType.KASI_UTALA
        || tile === TileType.KILI_LAWA || tile === TileType.KILI_SOWELI
        || isKasiPlaceholder(tile);
    const ladder = tile === TileType.LADDER || tile === TileType.LEAVES || tile === TileType.STICK
        || tile === TileType.WATER || tile === TileType.KASI_LINJA;
    const oneWay = tile === TileType.KIWEN_SUPA;
    return !passable && !ladder && !oneWay;
}

